import { useEffect, useState, useCallback, useRef } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { createPortal } from "react-dom";
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, Cell,
} from "recharts";
import { apiClient } from "../../lib/http/apiClient";
import { recApiClient } from "../../lib/http/recApiClient";
import { adminStorage } from "../../features/admin/service";
import ProfilePushSettingsEditor from "../../components/admin/ProfilePushSettingsEditor";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import Spinner from "../../components/ui/Spinner";
import RefreshButton from "../../components/admin/RefreshButton";
import { useAdminToast } from "../../context/AdminToastContext";

// ─── Types ────────────────────────────────────────────────────────────────────

interface AdminUser { id: string; name: string; email: string; role: string; created_at?: string; }

interface BuyerSignals {
  user_id: string;
  buyer_quality_score: number;
  primary_corridor?: string;
  secondary_corridors?: string[];
  avg_budget_band?: string;
  avg_pax_band?: string;
  avg_booking_value?: number;
  typical_group_size?: number;
  notification_open_rate?: number;
  effective_tier?: string;
  buyer_tier?: string;
  last_active_at?: string;
  profile_is_stale?: boolean;
  stale_since?: string;
  interest_graph?: {
    destinations?: Record<string, number>;
    tags?: Record<string, number>;
    activities?: Record<string, number>;
    categories?: Record<string, number>;
  };
  vertical_mix?: Record<string, number>;
  preferred_verticals?: string[];
  conversion_rate?: number;
  repeat_booking_rate?: number;
  junk_rate?: number;
  scroll_engagement?: number;
  total_rag_queries?: number;
  flyer_downloads?: number;
  session_count?: number;
  avg_session_duration_ms?: number;
  search_frequency?: number;
  feature_adoption?: number;
  avg_response_time_hours?: number;
  signal_absorption_weight?: number;
  wishlist_deal_ids?: string[];
  followed_dmc_ids?: string[];
  dismissed_deal_ids?: { deal_id: string; dismissed_at: string }[];
  dmc_affinity_scores?: Record<string, number>;
  total_saves?: number;
  total_contact_clicks?: number;
  total_leads_created?: number;
  seasonal_patterns?: Record<string, number>;
  // AI content — created (generation events)
  ai_itinerary_created_count?: number;
  ai_video_created_count?: number;
  ai_flyer_created_count?: number;
  ai_slides_created_count?: number;
  // AI content — engagement (download / share / regenerate events)
  ai_video_count?: number;
  ai_itinerary_count?: number;
  ai_slides_count?: number;
  flyer_shared_count?: number;
  flyer_regenerated_count?: number;
  video_shared_count?: number;
  ai_itinerary_shared_count?: number;
  ai_slides_shared_count?: number;
  total_ai_media_generated?: number;
  // Fresh RAG chat
  market_research_query_count?: number;
  // Session / device
  session_frequency_7d?: number;
  // Raw interaction counts
  total_enquiries_sent?: number;
  total_dm_threads?: number;
  total_deal_views?: number;
  total_deal_likes?: number;
  total_deal_downloads?: number;
  total_enquiry_drafts?: number;
  total_news_views?: number;
  total_news_likes?: number;
  total_dmc_profile_views?: number;
  saved_searches?: { query: string; last_run_at?: string }[];
  junk_marked_dmc_ids?: { dmc_id: string; marked_at: string }[];
  total_quotes_requested?: number;
}

interface SellerSignals {
  seller_id: string;
  seller_tier?: string;
  effective_tier?: string;
  dmc_quality_score?: number;
  acceptance_rate?: number;
  quote_rate?: number;
  lead_win_rate?: number;
  response_time_median_hours?: number;
  primary_corridors?: string[];
  primary_verticals?: string[];
  active_lead_count?: number;
  total_leads_count?: number;
  conversion_rate?: number;
  quota_status?: string;
  last_active_at?: string;
  dormancy_type?: string;
  avg_deal_price?: number;
  days_since_last_deal_update?: number;
  active_deals_count?: number;
  total_deals_uploaded_count?: number;
  junk_rate?: number;
  seller_rag_query_count?: number;
  flyer_download_count?: number;
  avg_scroll_engagement?: number;
  profile_view_count?: number;
  total_deal_views?: number;
  views_by_corridor?: Record<string, number>;
  view_to_lead_rate?: number;
  follower_count?: number;
  contact_click_count?: number;
  save_count?: number;
  cross_sell_enquiry_count?: number;
  lead_view_to_response_rate?: number;
  pair_affinity_scores?: Record<string, number>;
  // AI media (buyers downloading from seller's content)
  ai_video_count?: number;
  ai_itinerary_count?: number;
  ai_slides_count?: number;
  buyer_content_share_count?: number;
  // Buyer engagement counts
  total_deal_likes?: number;
  total_deal_downloads?: number;
  total_quotes_received?: number;
  news_view_count?: number;
  news_like_count?: number;
  total_enquiries_received?: number;
  total_dm_threads?: number;
  total_enquiry_drafts?: number;
  // Seller's own activity
  seller_session_count?: number;
  deal_created_count?: number;
  deal_updated_count?: number;
  deal_deleted_count?: number;
  ai_media_generated_count?: number;
  total_ai_tokens_used?: number;
  seller_notification_open_rate?: number;
}

interface FormulaARec {
  deal_id: string;
  seller_id?: string;
  seller_name?: string;
  recommendation_id?: string;
  score: number;
  components?: Record<string, number>;
  reason_strings?: string[];
  deal?: {
    title?: string;
    category?: string;
    countries?: string[];
    tags?: string[];
    price_min?: number;
    price_max?: number;
    dmc_quality_score?: number;
  };
}

interface BuyerNotif {
  log_id?: string;
  notification_id?: string;
  deal_id?: string;
  deal_title?: string;
  seller_id?: string;
  seller_name?: string;
  trigger_type?: string;
  title?: string;
  body?: string;
  formula_a_score?: number;
  tier_at_send?: string;
  sent_at?: string;
  outcome?: string;
  outcome_at?: string;
}

interface SellerNotif {
  log_id?: string;
  type?: string;
  lead_id?: string;
  buyer_id?: string;
  buyer_name?: string;
  deal_id?: string;
  deal_title?: string;
  title?: string;
  body?: string;
  sent_at?: string;
}

interface LeadItem {
  lead_id: string;
  formula_b_score?: number;
  tier?: string;
  lead?: {
    destination?: string | string[];
    category?: string;
    budget?: string;
    pax?: number;
    status?: string;
    created_at?: string;
    routed_at?: string;
    user_id?: string;
    buyer_name?: string;
  };
  components?: {
    urgency?: number;
    conversion_likelihood?: number;
    match_quality?: number;
    buyer_quality?: number;
    [key: string]: number | undefined;
  };
}

interface DealSuggestion {
  action: string;
  corridor?: string;
  vertical?: string;
  demand_score: number;
  reason: string;
}


interface HistPoint { t: number; label: string; score: number; openRate: number; conv: number; resp: number; [key: string]: number | string; }

// ─── Constants ────────────────────────────────────────────────────────────────

const TICK_MS      = 1_000;
const HISTORY_LEN  = 60;
const FORMULA_COLORS = ["#6366f1","#8b5cf6","#06b6d4","#10b981","#f59e0b","#f43f5e","#84cc16"];

const TIER_COLOR: Record<string, string> = {
  free:    "bg-gray-700 text-gray-300",
  starter: "bg-blue-900/60 text-blue-300",
  pro:     "bg-blue-900/60 text-blue-300",
  mid:     "bg-purple-900/60 text-purple-300",
  studio:  "bg-purple-900/60 text-purple-300",
  premium: "bg-amber-900/60 text-amber-300",
  elite:   "bg-amber-900/60 text-amber-300",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const tok = () => {
  const t = adminStorage.getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function ago(iso?: string | null): string {
  if (!iso) return "—";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// Time-window filter shared by per-user Notifications + Leads lists.
type TimeWindow = "24h" | "3d" | "7d" | "all";
const WINDOW_HOURS: Record<TimeWindow, number | null> = { "24h": 24, "3d": 72, "7d": 168, all: null };
function withinWindow(iso: string | undefined | null, window: TimeWindow): boolean {
  const hours = WINDOW_HOURS[window];
  if (hours == null) return true;
  if (!iso) return false;
  return Date.now() - new Date(iso).getTime() <= hours * 3_600_000;
}
const WindowTabs = ({ value, onChange }: { value: TimeWindow; onChange: (w: TimeWindow) => void }) => (
  <div className="flex gap-1 p-0.5 bg-gray-800/60 rounded-lg border border-gray-700/50 w-fit">
    {(["24h", "3d", "7d", "all"] as const).map((w) => (
      <button key={w} onClick={() => onChange(w)}
        className={`px-2.5 py-1 text-[10px] font-semibold rounded-md transition-all ${
          value === w ? "bg-indigo-500/20 text-indigo-300 border border-indigo-500/30" : "text-gray-500 hover:text-gray-300"
        }`}>
        {w === "all" ? "All time" : `Last ${w}`}
      </button>
    ))}
  </div>
);

function dealName(rec: FormulaARec): string {
  if (rec.deal?.title && rec.deal.title.length > 2) return rec.deal.title;
  if (rec.deal_id) return `Deal #${rec.deal_id.slice(-6)}`;
  return "Deal #?";
}

function leadLabel(l: LeadItem): string {
  const dest = l.lead?.destination;
  if (Array.isArray(dest) && dest.length) return dest.slice(0, 2).join(" / ");
  if (typeof dest === "string" && dest) return dest;
  if (l.lead?.category) return l.lead.category;
  if (l.lead?.budget) return `Budget: ${l.lead.budget}`;
  return `Lead #${l.lead_id?.slice(-5) ?? "?"}`;
}

function fmtInr(v?: number): string {
  if (!v) return "—";
  if (v >= 10_00_000) return `₹${(v / 10_00_000).toFixed(1)}Cr`;
  if (v >= 1_00_000) return `₹${(v / 1_00_000).toFixed(1)}L`;
  if (v >= 1_000) return `₹${(v / 1_000).toFixed(0)}k`;
  return `₹${v.toFixed(0)}`;
}

function fmtMs(ms?: number): string {
  if (!ms) return "—";
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

const pct = (v?: number) => `${Math.min(100, (v ?? 0) * 100).toFixed(1)}%`;

// ─── Micro UI ─────────────────────────────────────────────────────────────────

const Pulse = () => <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse flex-shrink-0" />;
const Spin  = ({ sz = 4 }: { sz?: number }) => (
  <svg className={`w-${sz} h-${sz} animate-spin text-indigo-400 flex-shrink-0`} fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);
const SH = ({ children, right }: { children: React.ReactNode; right?: React.ReactNode }) => (
  <div className="flex items-center justify-between mb-3">
    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">{children}</p>
    {right && <span className="text-xs text-gray-600">{right}</span>}
  </div>
);
// Renders its popup through a portal onto document.body — no ancestor's overflow-hidden/
// overflow-y-auto (tables, scroll panels, rounded cards) can ever clip it, since it's
// no longer a descendant of any of them in the DOM.
const InfoTip = ({ text }: { text: string }) => {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; flipUp: boolean } | null>(null);

  const show = () => {
    const r = anchorRef.current?.getBoundingClientRect();
    if (!r) return;
    const width = 256; // w-64
    const flipUp = r.bottom + 90 > window.innerHeight; // not enough room below
    let left = r.left + r.width / 2 - width / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
    setPos({ top: flipUp ? r.top - 8 : r.bottom + 8, left, flipUp });
  };
  const hide = () => setPos(null);

  return (
    <span ref={anchorRef} onMouseEnter={show} onMouseLeave={hide}
      className="relative inline-flex items-center cursor-default ml-1">
      <span className="text-gray-600 hover:text-gray-400 transition-colors text-[11px] select-none">ⓘ</span>
      {pos && createPortal(
        <div
          style={{ position: "fixed", top: pos.flipUp ? undefined : pos.top, bottom: pos.flipUp ? window.innerHeight - pos.top : undefined, left: pos.left }}
          className="z-[1000] w-64 bg-gray-950 border border-gray-700 rounded-lg p-2.5 shadow-xl pointer-events-none"
        >
          <span className="text-[11px] text-gray-300 leading-relaxed">{text}</span>
        </div>,
        document.body
      )}
    </span>
  );
};
const Card = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`rounded-xl border border-gray-800 bg-gray-900/60 p-4 ${className}`}>{children}</div>
);
const StatPill = ({ label, value, accent = "indigo", context, tooltip }: { label: string; value?: React.ReactNode; accent?: string; context?: string; tooltip?: string }) => (
  <div className="relative group bg-gray-800/60 rounded-lg p-3 flex flex-col gap-0.5 cursor-default">
    <p className="text-xs text-gray-500 flex items-center gap-1">
      {label}
      {tooltip && <span className="text-gray-700 group-hover:text-gray-500 transition-colors text-[10px]">ⓘ</span>}
    </p>
    <p className={`text-sm font-bold text-${accent}-300`}>{value ?? "—"}</p>
    {context && <p className="text-[10px] text-gray-600 leading-tight">{context}</p>}
    {tooltip && (
      <div className="absolute bottom-full left-0 mb-1.5 z-50 hidden group-hover:block w-56 bg-gray-950 border border-gray-700 rounded-lg p-2.5 shadow-xl pointer-events-none">
        <p className="text-[11px] text-gray-300 leading-relaxed">{tooltip}</p>
      </div>
    )}
  </div>
);
const SectionDivider = ({ label }: { label: string }) => (
  <div className="flex items-center gap-3 py-1">
    <div className="flex-1 h-px bg-gray-800" />
    <span className="text-xs font-bold text-gray-600 uppercase tracking-widest flex-shrink-0">{label}</span>
    <div className="flex-1 h-px bg-gray-800" />
  </div>
);

// ─── Chart helpers ────────────────────────────────────────────────────────────

const TOOLTIP_STYLE = {
  contentStyle: { background: "#111827", border: "1px solid #374151", borderRadius: 8, fontSize: 11 },
  labelStyle: { color: "#6b7280" },
  itemStyle: { color: "#e5e7eb" },
};

const LiveLineChart = ({
  data, dataKey, color, label, domain,
}: {
  data: HistPoint[];
  dataKey: keyof HistPoint;
  color: string;
  label: string;
  domain?: [number, number];
}) => {
  const latest = data.length ? Number(data[data.length - 1][dataKey]) : 0;
  const prev   = data.length > 1 ? Number(data[data.length - 2][dataKey]) : latest;
  const delta  = latest - prev;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline gap-2">
        <span className="text-xl font-mono font-bold text-white">{latest.toFixed(1)}</span>
        <span className={`text-xs font-mono ${delta >= 0 ? "text-emerald-400" : "text-red-400"}`}>
          {delta >= 0 ? "▲" : "▼"} {Math.abs(delta).toFixed(2)}
        </span>
        <span className="text-xs text-gray-500 ml-auto">{label}</span>
      </div>
      <ResponsiveContainer width="100%" height={72}>
        <AreaChart data={data} margin={{ top: 4, right: 0, left: -36, bottom: 0 }}>
          <defs>
            <linearGradient id={`grad-${String(dataKey)}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor={color} stopOpacity={0.3} />
              <stop offset="95%" stopColor={color} stopOpacity={0.0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
          <YAxis domain={domain ?? ["auto", "auto"]} hide />
          <XAxis dataKey="label" hide />
          <Tooltip {...TOOLTIP_STYLE} formatter={(v: any) => Number(v).toFixed(2)} />
          <Area
            type="monotone"
            dataKey={dataKey as string}
            stroke={color}
            strokeWidth={1.5}
            fill={`url(#grad-${String(dataKey)})`}
            dot={false}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const HorizBar = ({
  data, nameKey, valueKey, color = "#6366f1", maxVal, integerValues,
}: {
  data: Record<string, any>[];
  nameKey: string;
  valueKey: string;
  color?: string;
  maxVal?: number;
  integerValues?: boolean;
}) => {
  if (!data.length) return <p className="text-xs text-gray-600 text-center py-3">No data</p>;
  const max = maxVal ?? Math.max(...data.map((d) => Number(d[valueKey]) || 0), 1);
  return (
    <div className="space-y-2">
      {data.map((d, i) => {
        const val = Number(d[valueKey]) || 0;
        const w   = Math.min(100, Math.round((val / max) * 100));
        const display = integerValues
          ? String(Math.round(val))
          : typeof d[valueKey] === "number" && d[valueKey] < 1 ? pct(d[valueKey]) : Number(d[valueKey]).toFixed(2);
        return (
          <div key={i} className="flex items-center gap-2 text-xs">
            <span className="text-gray-300 w-32 truncate flex-shrink-0">{d[nameKey]}</span>
            <div className="flex-1 bg-gray-800 rounded-full h-2">
              <div className="h-2 rounded-full transition-all duration-700"
                style={{ width: `${w}%`, background: color }} />
            </div>
            <span className="text-gray-500 font-mono w-12 text-right flex-shrink-0">
              {display}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const VBar = ({
  data, xKey, yKey, color = "#6366f1", height = 140,
}: {
  data: Record<string, any>[];
  xKey: string;
  yKey: string;
  color?: string;
  height?: number;
}) => {
  if (!data.length) return <p className="text-xs text-gray-600 text-center py-3">No data</p>;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" horizontal />
        <XAxis dataKey={xKey} tick={{ fill: "#6b7280", fontSize: 10 }} interval={0} />
        <YAxis tick={{ fill: "#6b7280", fontSize: 10 }} />
        <Tooltip {...TOOLTIP_STYLE} />
        <Bar dataKey={yKey} radius={[3, 3, 0, 0]} isAnimationActive={false}>
          {data.map((_, i) => (
            <Cell key={i} fill={color} fillOpacity={0.8 - i * 0.04} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

const EngagementRadar = ({ data }: { data: { metric: string; value: number }[] }) => (
  <ResponsiveContainer width="100%" height={200}>
    <RadarChart data={data}>
      <PolarGrid stroke="#1f2937" />
      <PolarAngleAxis dataKey="metric" tick={{ fill: "#6b7280", fontSize: 10 }} />
      <Radar name="Signals" dataKey="value" stroke="#6366f1" fill="#6366f1" fillOpacity={0.25}
        isAnimationActive={false} />
      <Tooltip {...TOOLTIP_STYLE} formatter={(v: any) => `${Number(v).toFixed(1)}`} />
    </RadarChart>
  </ResponsiveContainer>
);

const SellerRadar = ({ data }: { data: { metric: string; value: number }[] }) => (
  <ResponsiveContainer width="100%" height={200}>
    <RadarChart data={data}>
      <PolarGrid stroke="#1f2937" />
      <PolarAngleAxis dataKey="metric" tick={{ fill: "#6b7280", fontSize: 10 }} />
      <Radar name="Signals" dataKey="value" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.25}
        isAnimationActive={false} />
      <Tooltip {...TOOLTIP_STYLE} formatter={(v: any) => `${Number(v).toFixed(1)}`} />
    </RadarChart>
  </ResponsiveContainer>
);

// ─── Combined Signal Chart ────────────────────────────────────────────────────

const BUYER_SIGNAL_LINES = [
  { key: "score",    name: "Quality Score", color: "#6366f1" },
  { key: "openRate", name: "Notif Open %",  color: "#10b981" },
  { key: "conv",     name: "Conversion %",  color: "#f59e0b" },
  { key: "resp",     name: "Response %",    color: "#06b6d4" },
] as const;

// Seller + buyer signal, plotted together — same tick cadence, so history[i]
// and bHistory[i] always share a timestamp and can be zipped by index.
const SELLER_BUYER_COMBINED_LINES = [
  { key: "sScore",    name: "DMC Quality Score (Seller)",   color: "#f59e0b" },
  { key: "bScore",    name: "Buyer Quality Score (Buyer)",  color: "#6366f1" },
  { key: "sOpenRate", name: "Acceptance % (Seller)",        color: "#10b981" },
  { key: "bOpenRate", name: "Notif Open % (Buyer)",         color: "#34d399" },
  { key: "sConv",     name: "Win Rate % (Seller)",          color: "#8b5cf6" },
  { key: "bConv",     name: "Conversion % (Buyer)",         color: "#c084fc" },
  { key: "sResp",     name: "Lead Response % (Seller)",     color: "#06b6d4" },
  { key: "bResp",     name: "Repeat Booking % (Buyer)",     color: "#67e8f9" },
] as const;

const CombinedSignalChart = ({
  data,
  lines,
  title = "All Signals — Combined Live View",
}: {
  data: Array<Record<string, number | string>>;
  lines: readonly { key: string; name: string; color: string }[];
  title?: string;
}) => {
  if (data.length < 2) return (
    <Card>
      <SH right="live · 1s">{title}</SH>
      <div className="h-40 flex items-center justify-center text-gray-600 text-xs">
        Collecting signal history… ({data.length}/{HISTORY_LEN} points)
      </div>
    </Card>
  );
  return (
    <Card>
      <SH right="live · 1s">{title}</SH>
      <div className="flex flex-wrap gap-3 mb-3">
        {lines.map((l) => (
          <div key={l.key} className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-0.5 rounded-full" style={{ background: l.color }} />
            <span className="text-xs text-gray-400">{l.name}</span>
            <span className="text-xs font-mono font-bold" style={{ color: l.color }}>
              {data.length ? Number(data[data.length - 1][l.key]).toFixed(1) : "—"}
            </span>
          </div>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={data} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
          <XAxis dataKey="label" tick={{ fill: "#4b5563", fontSize: 9 }} interval={Math.floor(data.length / 6)} />
          <YAxis domain={[0, 100]} tick={{ fill: "#4b5563", fontSize: 9 }} />
          <Tooltip {...TOOLTIP_STYLE} formatter={(v: any) => Number(v).toFixed(2)} />
          {lines.map((l) => (
            <Line key={l.key} type="monotone" dataKey={l.key} name={l.name}
              stroke={l.color} strokeWidth={1.5} dot={false} isAnimationActive={false} />
          ))}
        </LineChart>
      </ResponsiveContainer>
      <p className="text-xs text-gray-700 mt-1 text-right">
        all signals normalised 0–100 · last {data.length} ticks · ₹ INR (native)
      </p>
    </Card>
  );
};

// ─── Score / Formula Breakdown ───────────────────────────────────────────────

interface QScoreField {
  label: string;
  maxPts: number;
  currentPts: number;
  rawValue?: string;
  window?: string;
}

const ScoreBreakdownCard = ({
  title, totalScore, fields, accent,
}: {
  title: string; totalScore: number; fields: QScoreField[]; accent: string;
}) => (
  <Card>
    <div className="flex items-center justify-between mb-3">
      <div>
        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">{title}</p>
        <p className="text-[10px] text-gray-600 mt-0.5">fields · weights · live contributions</p>
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-2xl font-mono font-bold" style={{ color: accent }}>{totalScore.toFixed(1)}</p>
        <p className="text-[10px] text-gray-600">/ 100 pts</p>
      </div>
    </div>
    <div className="h-2 bg-gray-800 rounded-full overflow-hidden mb-4">
      <div className="h-2 rounded-full transition-all duration-700"
        style={{ width: `${Math.min(100, Math.max(0, totalScore))}%`, background: accent }} />
    </div>
    <div className="space-y-3">
      {fields.map((f, i) => {
        const isPenalty = f.maxPts < 0;
        const maxAbs = Math.abs(f.maxPts);
        const filledPct = maxAbs > 0 ? Math.min(100, (Math.abs(f.currentPts) / maxAbs) * 100) : 0;
        const barColor = isPenalty ? "#ef4444" : accent;
        const ptColor = isPenalty ? "#f87171" : f.currentPts > 0 ? "#34d399" : "#6b7280";
        return (
          <div key={i}>
            <div className="flex items-center justify-between text-xs mb-1 gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-gray-300 truncate">{f.label}</span>
                {f.window && (
                  <span className="px-1.5 py-0.5 bg-gray-800/80 text-gray-500 rounded text-[9px] flex-shrink-0">{f.window}</span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 font-mono text-[11px]">
                {f.rawValue && <span className="text-gray-500">{f.rawValue}</span>}
                <span style={{ color: ptColor }}>
                  {f.currentPts >= 0 ? "+" : ""}{f.currentPts.toFixed(1)} pts
                </span>
                <span className="text-gray-700">/ {isPenalty ? "-" : "+"}{maxAbs}</span>
              </div>
            </div>
            <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
              <div className="h-1.5 rounded-full transition-all duration-700"
                style={{ width: `${filledPct}%`, background: barColor }} />
            </div>
          </div>
        );
      })}
    </div>
  </Card>
);

interface FWeightField {
  label: string;
  weight: number;
  description: string;
}

const FormulaWeightsCard = ({
  title, subtitle, fields, accent,
}: {
  title: string; subtitle: string; fields: FWeightField[]; accent: string;
}) => (
  <Card>
    <SH right={subtitle}>{title}</SH>
    <div className="space-y-2.5">
      {fields.map((f, i) => (
        <div key={i}>
          <div className="flex items-center justify-between text-xs mb-1 gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-gray-300 flex-shrink-0">{f.label}</span>
              <span className="text-gray-600 text-[10px] truncate">{f.description}</span>
            </div>
            <span className="font-mono font-bold flex-shrink-0 text-xs" style={{ color: accent }}>
              {(f.weight * 100).toFixed(0)}%
            </span>
          </div>
          <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
            <div className="h-1.5 rounded-full transition-all duration-700"
              style={{ width: `${f.weight * 100}%`, background: accent }} />
          </div>
        </div>
      ))}
    </div>
  </Card>
);

// ─── Buyer Panel ──────────────────────────────────────────────────────────────

const BuyerPanel = ({ user, wsEvent }: { user: AdminUser; wsEvent?: Record<string, unknown> | null }) => {
  const [signals,    setSignals]    = useState<BuyerSignals | null>(null);
  const [recs,       setRecs]       = useState<FormulaARec[]>([]);
  const [notifs,     setNotifs]     = useState<BuyerNotif[]>([]);
  const [history,    setHistory]    = useState<HistPoint[]>([]);
  const [computing,  setComputing]  = useState(false);
  const [fetching,   setFetching]   = useState(false);
  const [lastAt,     setLastAt]     = useState<Date | null>(null);
  const [error,      setError]      = useState<string | null>(null);
  const { showError: showBuyerError } = useAdminToast();
  useEffect(() => { if (error) showBuyerError(error); }, [error, showBuyerError]);
  const [buyerTab,   setBuyerTab]   = useState<"analytics" | "feed">("analytics");
  const alive      = useRef(true);
  const signalsRef = useRef<BuyerSignals | null>(null);
  const historyRef = useRef<HistPoint[]>([]);

  useEffect(() => { signalsRef.current = signals; }, [signals]);

  const refresh = useCallback(async (doCompute: boolean, withRecs: boolean = doCompute) => {
    if (!alive.current) return;
    setError(null);
    if (doCompute) {
      setComputing(true);
      try { await recApiClient.post(`/api/buyer-intel/signals/compute/${user.id}`, {}, { headers: tok() }); }
      catch (e: any) {
        const msg = e?.code === "ERR_NETWORK" || e?.message?.includes("Network")
          ? "rec_server unreachable (port 8003 down?)"
          : e?.response?.data?.detail || e?.message || "compute failed";
        setError(msg);
      }
      if (!alive.current) return;
      setComputing(false);
    }
    setFetching(true);
    try {
      // Signals + notifs are cheap MongoDB reads, always fetched. Recommendations run
      // Formula A scoring, so they are pulled only when asked for — Compute Now, a
      // recs_updated push, or the Live Feed tab (the only view that renders them).
      const fetches: Promise<any>[] = [
        recApiClient.get<BuyerSignals>(`/api/buyer-intel/signals/${user.id}`, { headers: tok() }).catch((e) => { throw e; }),
        recApiClient.get<any>(`/api/buyer-intel/notifications/${user.id}?limit=100`, { headers: tok() }).catch(() => null),
      ];
      if (withRecs) {
        fetches.push(recApiClient.get<any>(`/api/buyer-intel/recommendations/${user.id}?limit=10`, { headers: tok() }).catch(() => null));
      }
      const [s, n, r] = await Promise.all(fetches);
      if (!alive.current) return;
      if (s?.data)  setSignals(s.data);
      if (n?.data)  setNotifs(Array.isArray(n.data.notifications) ? n.data.notifications : []);
      if (r?.data)  setRecs(Array.isArray(r.data.results) ? r.data.results : []);
      setLastAt(new Date());
    } catch (e: any) {
      if (!alive.current) return;
      const msg = e?.code === "ERR_NETWORK" || e?.message?.includes("Network")
        ? "rec_server unreachable — run: uvicorn rec_server:app --port 8003 --workers 1"
        : e?.response?.status === 404
          ? "No signals yet — click Compute Now"
          : e?.response?.data?.detail || e?.message || "fetch failed";
      setError(msg);
    }
    finally { if (alive.current) setFetching(false); }
  }, [user.id]);

  useEffect(() => {
    alive.current = true;
    historyRef.current = [];
    setSignals(null); setRecs([]); setNotifs([]); setHistory([]); setLastAt(null);
    refresh(false); // fetch existing signals on mount; use Compute Now for fresh compute
    const ti = setInterval(() => {
      const s = signalsRef.current;
      if (!s || !alive.current) return;
      const now = new Date();
      const pt: HistPoint = {
        t:        now.getTime(),
        label:    `${now.getMinutes().toString().padStart(2,"0")}:${now.getSeconds().toString().padStart(2,"0")}`,
        score:    Number(s.buyer_quality_score) || 0,
        openRate: (Number(s.notification_open_rate) || 0) * 100,
        conv:     (Number(s.conversion_rate) || 0) * 100,
        resp:     (Number(s.repeat_booking_rate) || 0) * 100,
      };
      historyRef.current = [...historyRef.current.slice(-(HISTORY_LEN - 1)), pt];
      setHistory([...historyRef.current]);
    }, TICK_MS);
    return () => { alive.current = false; clearInterval(ti); };
  }, [refresh]);

  // WS push — refresh when signals, recs, or notifications update for this user
  useEffect(() => {
    if (!wsEvent) return;
    const { type, user_id } = wsEvent as any;
    if (user_id !== user.id) return;
    if (type === "signals_computed" || type === "recs_updated" || type === "notification_added") {
      refresh(false, type === "recs_updated");
    }
  }, [wsEvent, user.id, refresh]);

  // Live Feed is the only tab that renders recommendations, and they are not part of
  // the cheap initial fetch — pull them when the tab is actually opened, otherwise it
  // permanently reads "No recommendations yet" until someone hits Compute Now.
  useEffect(() => {
    if (buyerTab === "feed") refresh(false, true);
  }, [buyerTab, refresh]);

  const tier   = (signals?.effective_tier || signals?.buyer_tier || "free").toLowerCase();
  const qScore = Number(signals?.buyer_quality_score) || 0;

  const dests = Object.entries(signals?.interest_graph?.destinations ?? {})
    .sort((a, b) => b[1] - a[1]).slice(0, 8)
    .map(([name, value]) => ({ name, value }));

  const cats = Object.entries(signals?.interest_graph?.categories ?? {})
    .sort((a, b) => b[1] - a[1]).slice(0, 8)
    .map(([name, value]) => ({ name, value }));

  const tags = Object.entries(signals?.interest_graph?.tags ?? {})
    .sort((a, b) => b[1] - a[1]).slice(0, 8)
    .map(([name, value]) => ({ name, value }));

  const activities = Object.entries(signals?.interest_graph?.activities ?? {})
    .sort((a, b) => b[1] - a[1]).slice(0, 8)
    .map(([name, value]) => ({ name, value }));

  const dmcAffinityEntries = Object.entries(signals?.dmc_affinity_scores ?? {});
  const dmcAffinityCount   = dmcAffinityEntries.length;
  const dmcAffinityAvg     = dmcAffinityCount > 0
    ? dmcAffinityEntries.reduce((sum, [, v]) => sum + Number(v), 0) / dmcAffinityCount : 0;
  const dmcAffinityMax     = dmcAffinityCount > 0
    ? Math.max(...dmcAffinityEntries.map(([, v]) => Number(v))) : 0;

  const seasonalData = Object.entries(signals?.seasonal_patterns ?? {})
    .map(([name, value]) => ({ name: name.slice(0, 3).toUpperCase(), value: Math.round(value * 100) }));

  const engagementRadar = [
    { metric: "Conv",       value: (Number(signals?.conversion_rate) || 0) * 100 },
    { metric: "Repeat",     value: (Number(signals?.repeat_booking_rate) || 0) * 100 },
    { metric: "Scroll",     value: Math.min(100, (Number(signals?.scroll_engagement) || 0) * 100) },
    { metric: "Features",   value: (Number(signals?.feature_adoption) || 0) * 100 },
    { metric: "Notif Open", value: (Number(signals?.notification_open_rate) || 0) * 100 },
    { metric: "AI Media",   value: Math.min(100, (Number(signals?.total_ai_media_generated) || 0) * 10) },
  ];

  const engagementBar = [
    { name: "Conversion",   pct: (Number(signals?.conversion_rate) || 0) * 100 },
    { name: "Repeat Book",  pct: (Number(signals?.repeat_booking_rate) || 0) * 100 },
    { name: "Notif Open",   pct: (Number(signals?.notification_open_rate) || 0) * 100 },
    { name: "Feature Use",  pct: (Number(signals?.feature_adoption) || 0) * 100 },
    { name: "Junk Rate",    pct: (Number(signals?.junk_rate) || 0) * 100 },
  ];

  const recBar = recs.slice(0, 8).map((r) => ({ name: dealName(r), score: Number(r.score) || 0 }));

  const aiCreatedTotal =
    (signals?.ai_itinerary_created_count ?? 0) + (signals?.ai_video_created_count ?? 0) +
    (signals?.ai_flyer_created_count ?? 0)     + (signals?.ai_slides_created_count ?? 0);
  const aiEngagementTotal =
    (signals?.ai_itinerary_count ?? 0)        + (signals?.ai_video_count ?? 0) +
    (signals?.ai_slides_count ?? 0)           + (signals?.flyer_downloads ?? 0) +
    (signals?.ai_itinerary_shared_count ?? 0) + (signals?.video_shared_count ?? 0) +
    (signals?.ai_slides_shared_count ?? 0)    + (signals?.flyer_shared_count ?? 0) +
    (signals?.flyer_regenerated_count ?? 0);

  // Welcome screen preview — fetched from /rag/chips/admin/preview (real backend output)
  const [previewData, setPreviewData] = useState<{ welcome_title: string; welcome_sub: string; chips: string[]; error?: string } | null>(null);
  useEffect(() => {
    setPreviewData(null);
    const firstName = user.name?.split(" ")[0] || "";
    apiClient.get<{ welcome_title: string; welcome_sub: string; chips: string[] }>(
      `/rag/chips/admin/preview?user_id=${user.id}&route=deals&limit=3&first_name=${encodeURIComponent(firstName)}&role=${encodeURIComponent(user.role)}&now_hour=${new Date().getHours()}`,
      { headers: tok() }
    ).then(res => setPreviewData(res.data)).catch((e: any) => {
      const msg = e?.response?.data?.detail || e?.message || "preview fetch failed";
      setPreviewData({ welcome_title: "", welcome_sub: "", chips: [], error: msg });
    });
  }, [user.id]);

  if (computing && !signals) return (
    <div className="flex flex-col items-center justify-center h-48 gap-3">
      <Spin sz={6} /><p className="text-gray-500 text-sm">Computing signals…</p>
    </div>
  );
  if (!signals) return (
    <div className="text-center py-16 space-y-3">
      {error
        ? <p className="text-red-400 text-sm font-mono bg-red-950/40 border border-red-800/40 rounded-lg px-4 py-3 max-w-md mx-auto">{error}</p>
        : <p className="text-gray-500 text-sm">No signals yet</p>
      }
      <button onClick={() => refresh(true)} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs rounded-lg">Compute Now</button>
    </div>
  );

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-indigo-500/20 flex items-center justify-center text-indigo-400 font-bold text-lg flex-shrink-0">
          {user.name[0]?.toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-semibold">{user.name}</p>
          <p className="text-gray-400 text-xs truncate">{user.email}</p>
          {user.created_at && <p className="text-gray-600 text-xs mt-0.5">Member since {ago(user.created_at)}</p>}
        </div>
        {signals.profile_is_stale && (
          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-900/40 text-red-400 border border-red-800/40 flex-shrink-0">STALE</span>
        )}
        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${TIER_COLOR[tier] ?? TIER_COLOR.free}`}>
          {tier.toUpperCase()}
        </span>
        <div className="flex items-center gap-1.5 text-xs text-gray-600 flex-shrink-0">
          {fetching || computing ? <Spin sz={3} /> : <Pulse />}
          {computing ? "computing…" : lastAt ? ago(lastAt.toISOString()) : "—"}
        </div>
        <button onClick={() => refresh(true)} title="Recompute"
          className="p-1.5 rounded-lg hover:bg-gray-700 text-gray-500 hover:text-indigo-400 transition-colors flex-shrink-0">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {/* Buyer tab bar */}
      <div className="flex gap-1 p-1 bg-gray-800/60 rounded-xl border border-gray-700/50">
        {(["analytics", "feed"] as const).map((t) => (
          <button key={t} onClick={() => setBuyerTab(t)}
            className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-all ${
              buyerTab === t
                ? t === "analytics"
                  ? "bg-indigo-500/20 text-indigo-300 border border-indigo-500/30"
                  : "bg-violet-500/20 text-violet-300 border border-violet-500/30"
                : "text-gray-500 hover:text-gray-300"
            }`}>
            {t === "analytics" ? "Buyer Analytics" : "Live Feed"}
          </button>
        ))}
      </div>

      {buyerTab === "analytics" && (<>

      {/* Buyer Quality Score Breakdown */}
      {(() => {
        const qFields: QScoreField[] = [
          { label: "Conversion Rate",     maxPts:  60, currentPts: (Number(signals.conversion_rate) || 0) * 60,  rawValue: pct(signals.conversion_rate),  window: "last 90d"  },
          { label: "Repeat Booking",      maxPts:  25, currentPts: (Number(signals.repeat_booking_rate) || 0) * 25, rawValue: pct(signals.repeat_booking_rate), window: "last 180d" },
          { label: "Junk Rate (penalty)", maxPts: -15, currentPts: -(Number(signals.junk_rate) || 0) * 15,       rawValue: pct(signals.junk_rate),         window: "last 90d"  },
        ];
        return <ScoreBreakdownCard title="Buyer Quality Score" totalScore={qScore} fields={qFields} accent="#6366f1" />;
      })()}

      {/* Formula A Weights */}
      <FormulaWeightsCard
        title="Formula A — Deal Score Weights"
        subtitle="deal scoring · 0–100"
        accent="#6366f1"
        fields={[
          { label: "Relevance",             weight: 0.30, description: "cosine similarity × entity match (geo, budget, pax)" },
          { label: "Quality",               weight: 0.20, description: "DMC quality score ÷ 100" },
          { label: "Affinity",              weight: 0.15, description: "dmc_affinity_scores[dmc_id] · decayed half-life 90d" },
          { label: "Freshness",             weight: 0.10, description: "deal age · exp decay half-life 30d" },
          { label: "Conversion Likelihood", weight: 0.10, description: "heuristic or XGBoost · P(buyer books)" },
          { label: "Intent Match",          weight: 0.10, description: "query intent ↔ deal vertical · 16 classes" },
          { label: "Diversity Need",        weight: 0.05, description: "penalty if same DMC ≥ 3× in results" },
        ]}
      />

      {/* Live trading charts — 4 charts */}
      <div className="grid grid-cols-2 gap-3">
        <Card>
          <SH right="live · 1s">Quality Score</SH>
          {history.length > 1
            ? <LiveLineChart data={history} dataKey="score" color="#6366f1" label="/ 100" domain={[0, 100]} />
            : <div className="h-20 flex items-center justify-center text-gray-600 text-xs">Collecting…</div>}
        </Card>
        <Card>
          <SH right="live · 1s">Notif Open Rate %</SH>
          {history.length > 1
            ? <LiveLineChart data={history} dataKey="openRate" color="#10b981" label="%" domain={[0, 100]} />
            : <div className="h-20 flex items-center justify-center text-gray-600 text-xs">Collecting…</div>}
        </Card>
        <Card>
          <SH right="live · 1s">Conversion %</SH>
          {history.length > 1
            ? <LiveLineChart data={history} dataKey="conv" color="#f59e0b" label="%" domain={[0, 100]} />
            : <div className="h-20 flex items-center justify-center text-gray-600 text-xs">Collecting…</div>}
        </Card>
        <Card>
          <SH right="live · 1s">Response %</SH>
          {history.length > 1
            ? <LiveLineChart data={history} dataKey="resp" color="#06b6d4" label="%" domain={[0, 100]} />
            : <div className="h-20 flex items-center justify-center text-gray-600 text-xs">Collecting…</div>}
        </Card>
      </div>

      {/* Stat pills — row 1: identity */}
      <div className="grid grid-cols-4 gap-2">
        <StatPill label="Quality Score"     value={qScore.toFixed(1)}                         accent="indigo"  context="/ 100 · daily compute"           tooltip="Composite buyer quality score (0–100). Weighted: conversion rate, repeat bookings, low junk rate. Higher = more weight in Formula A." />
        <StatPill label="Budget Band"       value={signals.avg_budget_band}                   accent="cyan"    context="avg booking value band"          tooltip="Buyer's typical spend range from last 90d transactions. Used to filter out deals outside their price range." />
        <StatPill label="Pax Band"          value={signals.avg_pax_band}                      accent="purple"  context="typical group size band"          tooltip="Typical group size from historical bookings. Helps match deals to the right group size." />
        <StatPill label="Avg Booking"       value={fmtInr(signals.avg_booking_value)}         accent="emerald" context="last 90d · booking avg"           tooltip="Average confirmed booking value in INR over last 90 days. Estimates buyer's purchasing power." />
      </div>

      {/* Stat pills — row 2: activity */}
      <div className="grid grid-cols-4 gap-2">
        <StatPill label="Group Size"        value={signals.typical_group_size?.toFixed(1)}    accent="sky"    context="last 90d · typical pax"           tooltip="Median pax count across bookings in last 90d. Helps Formula A surface deals suited to the buyer's usual group size." />
        <StatPill label="Search / day"      value={(signals.search_frequency ?? 0).toFixed(2)} accent="violet" context="last 30d · daily avg"            tooltip="Average AI search queries per day over last 30d. High frequency = active buyer with strong purchase intent." />
        <StatPill label="Feature Adoption"  value={pct(signals.feature_adoption)}             accent="amber"  context="/ 100% platform features"         tooltip="% of platform features actively used. High adoption = power user; boosts the engagement score component in Formula A." />
        <StatPill label="Signal Weight"     value={(signals.signal_absorption_weight ?? 1).toFixed(2)} accent="pink" context="1.0=self · 0.3=client mode" tooltip="Multiplier on all signals. 1.0 = buyer booking for themselves. 0.3 = client mode (agent booking on behalf of a client — signals are less reliable)." />
      </div>

      {/* Stat pills — row 3: content signals */}
      <div className="grid grid-cols-4 gap-2">
        <StatPill label="RAG Queries"       value={signals.total_rag_queries ?? 0}            accent="violet" context="last 30d · AI chat searches"     tooltip="Total AI-powered chat/search queries in last 30d. Each query is a destination or deal intent signal fed into the buyer's interest graph." />
        <StatPill label="Flyer Downloads"   value={signals.flyer_downloads ?? 0}              accent="sky"    context="last 30d · total downloads"       tooltip="Total marketing flyers downloaded. High count indicates the buyer is actively sharing deals with their own clients." />
        <StatPill label="Sessions"          value={signals.session_count ?? 0}                accent="indigo" context="last 30d · login sessions"        tooltip="Login sessions in last 30d. Recency and frequency feed the engagement score component in Formula A." />
        <StatPill label="Avg Session"       value={fmtMs(signals.avg_session_duration_ms)}    accent="cyan"   context="per session · active time"        tooltip="Average active time per session. Longer sessions = deeper deal research = higher purchase intent signal." />
      </div>

      {/* Stat pills — row 4: relationship signals */}
      <div className="grid grid-cols-4 gap-2">
        <StatPill label="Contact Clicks"    value={signals.total_contact_clicks ?? 0}         accent="emerald" context="last 90d · DMC contact taps"      tooltip="Times buyer clicked 'Contact DMC' in last 90d. Strong high-intent signal — directly boosts Formula A score." />
        <StatPill label="Leads Created"     value={signals.total_leads_created ?? 0}          accent="amber"   context="last 90d · quote requests"        tooltip="Total enquiry/quote requests sent to sellers in last 90d. Each lead = confirmed buyer intent on a specific deal." />
        <StatPill label="Followed DMCs"     value={(signals.followed_dmc_ids ?? []).length}   accent="purple"  context="total · no decay"                 tooltip="Number of DMC profiles the buyer follows. Followed DMCs get an affinity boost in Formula A deal ranking." />
        <StatPill label="Junk Rate"         value={pct(signals.junk_rate)}                    accent="red"     context="last 90d · 0% ideal · −15pts"     tooltip="% of buyer's leads that sellers marked as junk. High junk = low-quality enquiries. Penalizes Formula A score by up to −15 points." />
      </div>

      {/* Stat pills — row 5: wishlist + response */}
      <div className="grid grid-cols-4 gap-2">
        <StatPill label="Wishlist"          value={signals.wishlist_deal_ids?.length ?? 0}    accent="amber"  context="saved deals · 60d active"         tooltip="Deals saved to wishlist active within 60 days. Wishlisted deals get a recency boost in recommendation ranking." />
        <StatPill label="Dismissed Deals"   value={(signals.dismissed_deal_ids ?? []).length} accent="gray"   context="last 7d · suppressed from recs"   tooltip="Deals explicitly dismissed by the buyer in last 7d. These are fully suppressed from appearing in recommendations." />
        <StatPill label="Avg Response Time" value={signals.avg_response_time_hours != null ? `${signals.avg_response_time_hours.toFixed(1)}h` : "—"} accent="cyan" context="hrs to respond to lead" tooltip="Average hours taken to respond to a seller quote. Fast responders are higher-quality leads — sellers prioritize them." />
        <StatPill label="Corridor"          value={signals.primary_corridor}                  accent="emerald" context="top destination · last 12mo"      tooltip="Top destination corridor from search + booking history in last 12 months. Primary input for candidate deal retrieval in Formula A." />
      </div>

      {/* Last active + stale info */}
      {(signals.last_active_at || signals.secondary_corridors?.length) && (
        <Card>
          <SH>Profile Status</SH>
          <div className="flex flex-wrap gap-3 text-xs">
            <span className="text-gray-500">Last active: <span className="text-white">{ago(signals.last_active_at)}</span></span>
            {signals.profile_is_stale && signals.stale_since && (
              <span className="text-red-400">Stale since: {ago(signals.stale_since)}</span>
            )}
            {(signals.secondary_corridors ?? []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1 w-full">
                <span className="text-gray-500">Secondary corridors:</span>
                {(signals.secondary_corridors ?? []).map((c) => (
                  <span key={c} className="px-2 py-0.5 bg-indigo-900/30 text-indigo-300 rounded-full border border-indigo-800/40">{c}</span>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Engagement radar */}
      <Card>
        <SH>Engagement Radar</SH>
        <EngagementRadar data={engagementRadar} />
      </Card>

      {/* Engagement breakdown bar */}
      <Card>
        <SH right="% of activity">Engagement Breakdown</SH>
        <VBar data={engagementBar} xKey="name" yKey="pct" color="#6366f1" height={130} />
      </Card>

      {/* Seasonal patterns */}
      {seasonalData.length > 0 && (
        <Card>
          <SH right="travel seasonality">Seasonal Activity Patterns</SH>
          <VBar data={seasonalData} xKey="name" yKey="value" color="#06b6d4" height={120} />
        </Card>
      )}

      {/* Interest graph — 2x2 grid */}
      <div className="grid grid-cols-2 gap-3">
        <Card>
          <SH right={`${dests.length} destinations`}>Top Destinations</SH>
          <HorizBar data={dests} nameKey="name" valueKey="value" color="#6366f1" />
        </Card>
        <Card>
          <SH right={`${cats.length} categories`}>Interest Categories</SH>
          <HorizBar data={cats} nameKey="name" valueKey="value" color="#06b6d4" />
        </Card>
        {tags.length > 0 && (
          <Card>
            <SH right={`${tags.length} tags`}>Interest Tags</SH>
            <HorizBar data={tags} nameKey="name" valueKey="value" color="#8b5cf6" />
          </Card>
        )}
        {activities.length > 0 && (
          <Card>
            <SH right={`${activities.length} activities`}>Activities</SH>
            <HorizBar data={activities} nameKey="name" valueKey="value" color="#10b981" />
          </Card>
        )}
      </div>

      {/* DMC Affinity — aggregate (IDs not resolvable) */}
      {dmcAffinityCount > 0 && (
        <div className="grid grid-cols-3 gap-2">
          <StatPill label="DMCs Engaged"   value={dmcAffinityCount}            accent="amber" />
          <StatPill label="Avg Affinity"   value={dmcAffinityAvg.toFixed(3)}   accent="indigo" />
          <StatPill label="Top Affinity"   value={dmcAffinityMax.toFixed(3)}   accent="emerald" />
        </div>
      )}

      {/* Top Pick hero */}
      {recs.length > 0 && (
        <Card className="border-indigo-500/30 bg-indigo-950/20">
          <SH right="Formula A · #1">Top Recommendation</SH>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center flex-shrink-0 text-lg">🏆</div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-sm">{dealName(recs[0])}</p>
              <div className="flex items-center gap-2 mt-1.5">
                <div className="flex-1 bg-gray-800 rounded-full h-1.5">
                  <div className="h-1.5 rounded-full bg-indigo-500 transition-all duration-700" style={{ width: `${Math.min(100, recs[0].score)}%` }} />
                </div>
                <span className="text-indigo-300 font-mono text-xs flex-shrink-0">{recs[0].score.toFixed(1)} / 100</span>
              </div>
              {(recs[0].reason_strings ?? []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(recs[0].reason_strings ?? []).map((rs, i) => (
                    <span key={i} className="px-2 py-0.5 bg-indigo-900/40 text-indigo-300 text-xs rounded-full border border-indigo-800/40">{rs}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Formula A Recs */}
      {recBar.length > 0 && (
        <Card>
          <SH right={`${recBar.length} deals`}>Formula A — Live Recommendations</SH>
          <div className="space-y-1.5">
            {recBar.map((r, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className="text-gray-400 w-36 truncate flex-shrink-0">{r.name}</span>
                <div className="flex-1 bg-gray-800 rounded-full h-2.5">
                  <div className="h-2.5 rounded-full transition-all duration-700"
                    style={{ width: `${Math.min(100, r.score)}%`, background: FORMULA_COLORS[i % FORMULA_COLORS.length] }} />
                </div>
                <span className="font-mono text-gray-300 w-10 text-right flex-shrink-0">{r.score.toFixed(1)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Component breakdown for top rec */}
      {recs[0]?.components && Object.keys(recs[0].components).length > 0 && (
        <Card>
          <SH right={dealName(recs[0])}>Formula A — Component Breakdown (Top Deal)</SH>
          <VBar
            data={Object.entries(recs[0].components).map(([k, v]) => ({
              name: k.replace(/_/g, " "),
              val: typeof v === "object" && v !== null
                ? Math.round(Number((v as any).score ?? 0) * 100)
                : Number(v) || 0,
            }))}
            xKey="name" yKey="val" color="#8b5cf6" height={130}
          />
        </Card>
      )}

      {/* Combined signal chart */}
      <CombinedSignalChart data={history} lines={BUYER_SIGNAL_LINES} />

      {/* AI Content — three separate concerns that used to sit in one undifferentiated
          block: what the buyer generated, what they did with it afterwards, and their
          fresh-RAG chat usage. */}
      <SectionDivider label="AI Content" />

      <Card>
        <SH right={`${signals.total_ai_media_generated ?? 0} generated`}>
          Created
          <InfoTip text="Successful AI generations, split by media type. These count creations only — a buyer can generate media and never download or share it, which is what the Engagement group tracks separately." />
        </SH>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <StatPill label="Itineraries" value={signals.ai_itinerary_created_count ?? 0} accent="violet" context="generated" tooltip="AI itineraries this buyer generated. Highest-weight AI intent signal (weight 8) in the interest graph — an itinerary means they are actively planning a specific trip." />
          <StatPill label="Videos"      value={signals.ai_video_created_count ?? 0}     accent="pink"   context="generated" tooltip="AI promo videos this buyer generated. Feeds the interest graph at weight 5 — usually produced to pitch a destination to their own clients." />
          <StatPill label="Flyers"      value={signals.ai_flyer_created_count ?? 0}     accent="indigo" context="generated" tooltip="AI marketing flyers this buyer generated. Feeds the interest graph at weight 6 — strong signal they are reselling the destination." />
          <StatPill label="Slides"      value={signals.ai_slides_created_count ?? 0}    accent="sky"    context="generated" tooltip="AI slide decks this buyer generated. Feeds the interest graph at weight 4." />
        </div>
        {aiCreatedTotal !== (signals.total_ai_media_generated ?? 0) && (
          <p className="text-[10px] text-gray-600 mt-2">
            {aiCreatedTotal} of {signals.total_ai_media_generated ?? 0} generations carry a known media type
          </p>
        )}
      </Card>

      <Card>
        <SH right={`${aiEngagementTotal} actions`}>
          Engagement
          <InfoTip text="What the buyer did with generated media afterwards. Downloads and shares are separate events from generation — high share counts mean the buyer is putting this content in front of their own clients." />
        </SH>
        <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Downloaded</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <StatPill label="Itineraries" value={signals.ai_itinerary_count ?? 0} accent="violet" context="downloaded" />
          <StatPill label="Videos"      value={signals.ai_video_count ?? 0}     accent="pink"   context="downloaded" />
          <StatPill label="Flyers"      value={signals.flyer_downloads ?? 0}    accent="indigo" context="downloaded" />
          <StatPill label="Slides"      value={signals.ai_slides_count ?? 0}    accent="sky"    context="downloaded" />
        </div>
        <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider mt-3 mb-1.5">Shared</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <StatPill label="Itineraries" value={signals.ai_itinerary_shared_count ?? 0} accent="violet" context="shared" />
          <StatPill label="Videos"      value={signals.video_shared_count ?? 0}        accent="pink"   context="shared" />
          <StatPill label="Flyers"      value={signals.flyer_shared_count ?? 0}        accent="indigo" context="shared" />
          <StatPill label="Slides"      value={signals.ai_slides_shared_count ?? 0}    accent="sky"    context="shared" />
        </div>
        <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider mt-3 mb-1.5">Regenerated</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <StatPill label="Flyers" value={signals.flyer_regenerated_count ?? 0} accent="gray" context="regenerated" tooltip="Times the buyer re-ran flyer generation. Repeated regeneration on the same deal can indicate the first output missed what they wanted." />
        </div>
      </Card>

      <Card>
        <SH>
          Fresh RAG
          <InfoTip text="Fresh-RAG chat activity — how many chat turns this buyer ran, and how many of those came from the Market Research tab." />
        </SH>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <StatPill label="Chat Queries"    value={signals.total_rag_queries ?? 0}                       accent="violet" context="total chat turns"          tooltip="Total AI chat/search turns. Each turn is a destination or deal-intent signal fed into the buyer's interest graph." />
          <StatPill label="Market Research" value={signals.market_research_query_count ?? 0}             accent="cyan"   context="of which market-research"   tooltip="Chat turns issued from the Market Research tab. A subset of Chat Queries, not an additional count." />
        </div>
      </Card>

      {/* Session / Device */}
      <SectionDivider label="Sessions &amp; Device" />
      <div className="grid grid-cols-2 gap-2">
        <StatPill label="Sessions (7d)"     value={signals.session_frequency_7d ?? 0}      accent="sky"     context="last 7 days" />
        <StatPill label="Avg Session"       value={fmtMs(signals.avg_session_duration_ms)} accent="cyan"    context="active time" />
      </div>

      {/* Raw Interaction Counts */}
      <SectionDivider label="Raw Interactions" />
      <div className="grid grid-cols-4 gap-2">
        <StatPill label="Enquiries Sent"    value={signals.total_enquiries_sent ?? 0}      accent="amber"   context="total enquiries" />
        <StatPill label="DM Threads"        value={signals.total_dm_threads ?? 0}          accent="emerald" context="conversations opened" />
        <StatPill label="Quote Requests"    value={signals.total_quotes_requested ?? 0}    accent="violet"  context="formal quotes" />
        <StatPill label="Deal Views"        value={signals.total_deal_views ?? 0}          accent="indigo"  context="deals viewed" />
      </div>
      <div className="grid grid-cols-4 gap-2">
        <StatPill label="Deal Likes"        value={signals.total_deal_likes ?? 0}          accent="pink"    context="deals liked" />
        <StatPill label="Deal Downloads"    value={signals.total_deal_downloads ?? 0}      accent="sky"     context="deal PDFs" />
        <StatPill label="Enquiry Drafts"    value={signals.total_enquiry_drafts ?? 0}      accent="gray"    context="pre-enquiry drafts" />
        <StatPill label="News Views"        value={signals.total_news_views ?? 0}          accent="cyan"    context="news articles viewed" />
      </div>
      <div className="grid grid-cols-4 gap-2">
        <StatPill label="News Likes"        value={signals.total_news_likes ?? 0}          accent="pink"    context="news liked" />
        <StatPill label="Profile Views"     value={signals.total_dmc_profile_views ?? 0}   accent="amber"   context="DMC profiles viewed" />
      </div>

      {/* Preferred verticals */}
      {(signals.preferred_verticals ?? []).length > 0 && (
        <Card>
          <SH right={`${(signals.preferred_verticals ?? []).length}`}>Preferred Verticals</SH>
          <div className="flex flex-wrap gap-1.5">
            {(signals.preferred_verticals ?? []).map((v) => (
              <span key={v} className="px-2.5 py-1 bg-violet-900/30 text-violet-300 text-xs rounded-full border border-violet-800/40">{v}</span>
            ))}
          </div>
        </Card>
      )}

      {/* Welcome screen preview */}
      <Card>
        <SH>Welcome Screen Preview</SH>
        <p className="text-[11px] text-gray-500 -mt-1 mb-3">Exactly what this user sees on RAG chat welcome screen</p>
        {!previewData ? (
          <div className="text-center py-4 text-gray-600 text-xs">Loading preview…</div>
        ) : previewData.error ? (
          <div className="text-center py-4 text-red-400 text-xs font-mono">{previewData.error}</div>
        ) : previewData.chips.length === 0 ? (
          <div className="text-center py-4 text-gray-600 text-xs">No signals yet — user sees admin fallback chips</div>
        ) : (
          <div className="bg-gray-900/60 border border-gray-700/40 rounded-xl p-4 space-y-3">
            <p className="text-white text-sm font-semibold text-center leading-snug">{previewData.welcome_title}</p>
            <p className="text-gray-400 text-xs text-center leading-relaxed">{previewData.welcome_sub}</p>
            <div className="flex flex-wrap gap-1.5 justify-center">
              {previewData.chips.map((chip) => (
                <span key={chip} className="px-3 py-1 border border-gray-600/60 rounded-full text-xs text-gray-300 bg-gray-800/40">{chip}</span>
              ))}
            </div>
          </div>
        )}
      </Card>

      </>)}

      {/* ── Live Feed tab ── */}
      {buyerTab === "feed" && (<>

        {/* Deal recommendation cards — what buyer actually sees */}
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-1">
            Recommended Deals — Formula A ({recs.length})
          </p>
          {recs.length === 0 && (
            fetching
              ? <div className="flex items-center justify-center gap-2 py-8 text-gray-600 text-sm"><Spin /> Scoring deals…</div>
              : <div className="text-center py-8 text-gray-600 text-sm">No recommendations for this buyer</div>
          )}
          {recs.map((rec, i) => (
            <div key={rec.deal_id || i}
              className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-3 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-semibold truncate">
                    {rec.deal?.title || `Deal #${rec.deal_id?.slice(-6) ?? "?"}`}
                  </p>
                  {rec.seller_name && (
                    <p className="text-amber-300 text-[11px] mt-0.5 truncate">{rec.seller_name}</p>
                  )}
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(rec.deal?.countries ?? []).map((c) => (
                      <span key={c} className="px-2 py-0.5 bg-indigo-900/40 text-indigo-300 text-[10px] rounded-full border border-indigo-800/40">{c}</span>
                    ))}
                    {rec.deal?.category && (
                      <span className="px-2 py-0.5 bg-gray-700/60 text-gray-400 text-[10px] rounded-full">{rec.deal.category}</span>
                    )}
                    {(rec.deal?.tags ?? []).slice(0, 3).map((t) => (
                      <span key={t} className="px-2 py-0.5 bg-gray-700/40 text-gray-500 text-[10px] rounded-full">{t}</span>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span className={`px-2.5 py-1 rounded-lg text-xs font-bold border ${
                    rec.score >= 70 ? "bg-emerald-900/40 text-emerald-300 border-emerald-700/40"
                    : rec.score >= 40 ? "bg-amber-900/40 text-amber-300 border-amber-700/40"
                    : "bg-gray-800 text-gray-400 border-gray-700"
                  }`}>{(Number(rec.score) || 0).toFixed(1)}</span>
                  {rec.deal?.price_min != null && (
                    <span className="text-[10px] text-gray-500">
                      {fmtInr(rec.deal.price_min)}{rec.deal.price_max ? ` – ${fmtInr(rec.deal.price_max)}` : "+"}
                    </span>
                  )}
                </div>
              </div>
              {(rec.reason_strings ?? []).length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {(rec.reason_strings ?? []).map((rs, j) => (
                    <span key={j} className="px-2 py-0.5 bg-violet-900/30 text-violet-300 text-[10px] rounded-full border border-violet-800/30">{rs}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Notification log — what was actually sent to buyer. Full user-wise history
            with time-window filters + edit lives in the global Notifications tab. */}
        <div className="space-y-2 mt-2">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-1">
            Sent Notifications ({notifs.length})
          </p>
          {notifs.length === 0 && (
            <div className="text-center py-6 text-gray-600 text-sm">No notifications sent yet</div>
          )}
          {notifs.map((n, i) => {
            const TRIGGER_COLOR: Record<string, string> = {
              new_listing:  "bg-indigo-900/40 text-indigo-300 border-indigo-800/40",
              idle_3d:      "bg-amber-900/40 text-amber-300 border-amber-800/40",
              price_drop:   "bg-emerald-900/40 text-emerald-300 border-emerald-800/40",
              early_access: "bg-violet-900/40 text-violet-300 border-violet-800/40",
            };
            const OUTCOME_COLOR: Record<string, string> = {
              clicked:   "text-emerald-400",
              engaged:   "text-sky-400",
              dismissed: "text-red-400",
              junk_marked: "text-red-500",
              ignored:   "text-gray-600",
            };
            return (
              <div key={n.notification_id || i}
                className="bg-gray-800/40 border border-gray-700/40 rounded-xl px-3 py-2.5 space-y-1.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border flex-shrink-0 ${TRIGGER_COLOR[n.trigger_type ?? ""] ?? "bg-gray-800 text-gray-500 border-gray-700"}`}>
                    {n.trigger_type ?? "unknown"}
                  </span>
                  {n.tier_at_send && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-gray-800 text-gray-400 border border-gray-700 flex-shrink-0">
                      {n.tier_at_send}
                    </span>
                  )}
                  {n.formula_a_score != null && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-indigo-900/40 text-indigo-300 border border-indigo-800/40 flex-shrink-0">
                      A: {n.formula_a_score.toFixed(1)}
                    </span>
                  )}
                  <span className={`text-xs ml-auto flex-shrink-0 ${OUTCOME_COLOR[n.outcome ?? ""] ?? "text-gray-600"}`}>
                    {n.outcome ?? "pending"}
                  </span>
                  <span className="text-[10px] text-gray-700 flex-shrink-0">{n.sent_at ? ago(n.sent_at) : "—"}</span>
                </div>
                {n.title && <p className="text-white text-xs font-semibold">{n.title}</p>}
                {n.body  && <p className="text-gray-400 text-[11px] leading-relaxed">{n.body}</p>}
                <div className="flex flex-wrap gap-2 text-[10px]">
                  {(n.deal_title || n.deal_id) && (
                    <span className="text-gray-500">Deal: <span className="text-gray-300">{n.deal_title || `#${n.deal_id?.slice(-6)}`}</span></span>
                  )}
                  {n.seller_name && (
                    <span className="text-gray-500">Seller: <span className="text-amber-300">{n.seller_name}</span></span>
                  )}
                  {!n.seller_name && n.seller_id && (
                    <span className="text-gray-600">Seller ID: {n.seller_id.slice(-8)}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

      </>)}

      <div className="flex items-center justify-center gap-1.5 text-xs text-gray-700">
        <Pulse /> live · charts 1s · ws-push
        {lastAt && ` · ${lastAt.toLocaleTimeString()}`}
      </div>
    </div>
  );
};

// ─── Seller Panel ─────────────────────────────────────────────────────────────

const SellerPanel = ({ user, wsEvent }: { user: AdminUser; wsEvent?: Record<string, unknown> | null }) => {
  const [signals,      setSignals]      = useState<SellerSignals | null>(null);
  const [buyerSigs,    setBuyerSigs]    = useState<BuyerSignals | null>(null);
  const [buyerRecs,    setBuyerRecs]    = useState<FormulaARec[]>([]);
  const [buyerNotifs,  setBuyerNotifs]  = useState<BuyerNotif[]>([]);
  const [leads,        setLeads]        = useState<LeadItem[]>([]);
  const [suggestions,  setSuggestions]  = useState<DealSuggestion[]>([]);
  const [sellerNotifs, setSellerNotifs] = useState<SellerNotif[]>([]);
  const [history,      setHistory]      = useState<HistPoint[]>([]);
  const [bHistory,     setBHistory]     = useState<HistPoint[]>([]);
  const [computing,    setComputing]    = useState(false);
  const [fetching,     setFetching]     = useState(false);
  const [lastAt,       setLastAt]       = useState<Date | null>(null);
  const [error,        setError]        = useState<string | null>(null);
  const { showError: showSellerError } = useAdminToast();
  useEffect(() => { if (error) showSellerError(error); }, [error, showSellerError]);
  const [sellerTab,    setSellerTab]    = useState<"seller" | "feed">("seller");
  const [leadBusyId,   setLeadBusyId]   = useState<string | null>(null);
  const [leadActionMsg, setLeadActionMsg] = useState<string | null>(null);
  const [editingLeadId, setEditingLeadId] = useState<string | null>(null);
  const [editLeadDest,  setEditLeadDest]  = useState("");
  const [editLeadCat,   setEditLeadCat]   = useState("");
  const [editLeadBudget, setEditLeadBudget] = useState("");
  const [editLeadPax,   setEditLeadPax]   = useState("");
  const alive        = useRef(true);
  const signalsRef   = useRef<SellerSignals | null>(null);
  const buyerSigsRef = useRef<BuyerSignals | null>(null);
  const historyRef   = useRef<HistPoint[]>([]);
  const bHistoryRef  = useRef<HistPoint[]>([]);

  useEffect(() => { signalsRef.current   = signals;   }, [signals]);
  useEffect(() => { buyerSigsRef.current = buyerSigs; }, [buyerSigs]);

  const refresh = useCallback(async (doCompute: boolean) => {
    if (!alive.current) return;
    setError(null);
    if (doCompute) {
      setComputing(true);
      try {
        await Promise.all([
          recApiClient.post(`/api/seller-intel/signals/compute/${user.id}`, {}, { headers: tok() }),
          recApiClient.post(`/api/buyer-intel/signals/compute/${user.id}`,  {}, { headers: tok() }).catch(() => null),
        ]);
      } catch (e: any) {
        const msg = e?.code === "ERR_NETWORK" || e?.message?.includes("Network")
          ? "rec_server unreachable (port 8003 down?)"
          : e?.response?.data?.detail || e?.message || "compute failed";
        setError(msg);
      }
      if (!alive.current) return;
      setComputing(false);
    }
    setFetching(true);
    try {
      // Initial load (doCompute=false): signals+notifs only — fast MongoDB reads.
      // Compute Now (doCompute=true): also fetch leads, suggestions, buyer recs (scoring).
      const baseFetches = [
        recApiClient.get<SellerSignals>(`/api/seller-intel/signals/${user.id}`,      { headers: tok() }).catch((e) => { throw e; }),
        recApiClient.get<BuyerSignals>(`/api/buyer-intel/signals/${user.id}`,        { headers: tok() }).catch(() => null),
        recApiClient.get<any>(`/api/seller-intel/notifications/${user.id}?limit=100`, { headers: tok() }).catch(() => null),
        recApiClient.get<any>(`/api/buyer-intel/notifications/${user.id}?limit=100`,  { headers: tok() }).catch(() => null),
      ];
      const scoringFetches = doCompute ? [
        recApiClient.get<any>(`/api/seller-intel/lead-inbox/${user.id}?limit=100`,      { headers: tok() }).catch(() => null),
        recApiClient.get<any>(`/api/seller-intel/deal-suggestions/${user.id}?limit=8`, { headers: tok() }).catch(() => null),
        recApiClient.get<any>(`/api/buyer-intel/recommendations/${user.id}?limit=10`,  { headers: tok() }).catch(() => null),
      ] : [];
      const [s, bs, sn, bn, l, sg, br] = await Promise.all([...baseFetches, ...scoringFetches]);
      if (!alive.current) return;
      if (s?.data)  setSignals(s.data);
      if (bs?.data && bs.data.user_id) setBuyerSigs(bs.data);
      if (sn?.data) setSellerNotifs(Array.isArray(sn.data.notifications) ? sn.data.notifications : []);
      if (bn?.data) setBuyerNotifs(Array.isArray(bn.data.notifications) ? bn.data.notifications : []);
      if (l?.data)  setLeads(Array.isArray(l.data.leads) ? l.data.leads : Array.isArray(l.data) ? l.data : []);
      if (sg?.data) setSuggestions(Array.isArray(sg.data.suggestions) ? sg.data.suggestions : []);
      if (br?.data) setBuyerRecs(Array.isArray(br.data.results) ? br.data.results : []);
      setLastAt(new Date());
    } catch (e: any) {
      if (!alive.current) return;
      const msg = e?.code === "ERR_NETWORK" || e?.message?.includes("Network")
        ? "rec_server unreachable — run: uvicorn rec_server:app --port 8003 --workers 1"
        : e?.response?.status === 404
          ? "No signals yet — click Compute Now"
          : e?.response?.data?.detail || e?.message || "fetch failed";
      setError(msg);
    }
    finally { if (alive.current) setFetching(false); }
  }, [user.id]);

  const rerouteLead = useCallback(async (leadId: string) => {
    setLeadBusyId(leadId); setLeadActionMsg(null);
    try {
      await recApiClient.post(`/api/admin/rec-engine/leads/${leadId}/reroute`, {}, { headers: tok() });
      setLeadActionMsg(`Lead ${leadId.slice(-6)} rerouted`);
      refresh(false);
    } catch (e: any) {
      setLeadActionMsg(e?.response?.data?.detail || e?.message || "reroute failed");
    } finally {
      setLeadBusyId(null);
    }
  }, [refresh]);

  const stopLead = useCallback(async (leadId: string) => {
    setLeadBusyId(leadId); setLeadActionMsg(null);
    try {
      await recApiClient.post(`/api/admin/rec-engine/leads/${leadId}/stop`, {}, { headers: tok() });
      setLeadActionMsg(`Lead ${leadId.slice(-6)} held (stopped)`);
      refresh(false);
    } catch (e: any) {
      setLeadActionMsg(e?.response?.data?.detail || e?.message || "stop failed");
    } finally {
      setLeadBusyId(null);
    }
  }, [refresh]);

  const startLeadEdit = (l: LeadItem) => {
    setEditingLeadId(l.lead_id);
    const dest = l.lead?.destination;
    setEditLeadDest(Array.isArray(dest) ? dest.join(", ") : (dest || ""));
    setEditLeadCat(l.lead?.category || "");
    setEditLeadBudget(l.lead?.budget || "");
    setEditLeadPax(l.lead?.pax != null ? String(l.lead.pax) : "");
  };

  const saveLeadEdit = async (leadId: string) => {
    setLeadBusyId(leadId); setLeadActionMsg(null);
    try {
      const body: Record<string, unknown> = {
        destination: editLeadDest.split(",").map((s) => s.trim()).filter(Boolean),
        category: editLeadCat,
        budget: editLeadBudget,
      };
      if (editLeadPax.trim()) body.pax = Number(editLeadPax);
      await recApiClient.patch(`/api/admin/rec-engine/leads/${leadId}`, body, { headers: tok() });
      setEditingLeadId(null);
      setLeadActionMsg(`Lead ${leadId.slice(-6)} updated`);
      refresh(false);
    } catch (e: any) {
      setLeadActionMsg(e?.response?.data?.detail || e?.message || "edit failed");
    } finally {
      setLeadBusyId(null);
    }
  };

  useEffect(() => {
    alive.current = true;
    historyRef.current  = [];
    bHistoryRef.current = [];
    setSignals(null); setBuyerSigs(null); setBuyerRecs([]); setBuyerNotifs([]); setLeads([]); setSuggestions([]); setHistory([]); setBHistory([]); setLastAt(null);
    refresh(false); // fetch existing signals on mount; use Compute Now for fresh compute
    const ti = setInterval(() => {
      if (!alive.current) return;
      const now   = new Date();
      const label = `${now.getMinutes().toString().padStart(2,"0")}:${now.getSeconds().toString().padStart(2,"0")}`;
      const s = signalsRef.current;
      if (s) {
        const pt: HistPoint = {
          t: now.getTime(), label,
          score:    Math.min(100, Number(s.dmc_quality_score) || 0),
          openRate: Math.min(100, (Number(s.acceptance_rate) || 0) * 100),
          conv:     Math.min(100, (Number(s.lead_win_rate) || 0) * 100),
          resp:     Math.min(100, (Number(s.lead_view_to_response_rate) || 0) * 100),
        };
        historyRef.current = [...historyRef.current.slice(-(HISTORY_LEN - 1)), pt];
        setHistory([...historyRef.current]);
      }
      const b = buyerSigsRef.current;
      if (b) {
        const bpt: HistPoint = {
          t: now.getTime(), label,
          score:    Number(b.buyer_quality_score) || 0,
          openRate: (Number(b.notification_open_rate) || 0) * 100,
          conv:     (Number(b.conversion_rate) || 0) * 100,
          resp:     (Number(b.repeat_booking_rate) || 0) * 100,
        };
        bHistoryRef.current = [...bHistoryRef.current.slice(-(HISTORY_LEN - 1)), bpt];
        setBHistory([...bHistoryRef.current]);
      }
    }, TICK_MS);
    return () => { alive.current = false; clearInterval(ti); };
  }, [refresh]);

  // WS push — refresh fetch when signals_computed fires for this user
  // WS push — refresh when signals, leads, or notifications update for this seller
  useEffect(() => {
    if (!wsEvent) return;
    const { type, user_id, seller_id } = wsEvent as any;
    if (
      (type === "signals_computed"  && user_id   === user.id) ||
      (type === "lead_routed"       && seller_id === user.id) ||
      (type === "notification_added" && user_id  === user.id)
    ) {
      refresh(false);
    }
  }, [wsEvent, user.id, refresh]);

  const tier = (signals?.effective_tier || signals?.seller_tier || "free").toLowerCase();

  const leadBar = leads.slice(0, 10).map((l) => ({
    name:  leadLabel(l),
    score: Number(l.formula_b_score) || 0,
    conv:  Number(l.components?.conversion_likelihood) || 0,
  }));

  const suggBar = suggestions.map((s) => ({
    name:   [s.corridor, s.vertical].filter(Boolean).join(" / ") || s.action,
    demand: Number(s.demand_score) || 0,
  }));

  const viewsByCorridor = Object.entries(signals?.views_by_corridor ?? {})
    .sort((a, b) => b[1] - a[1]).slice(0, 8)
    .map(([name, value]) => ({ name, value }));

  const pairAffinityEntries = Object.entries(signals?.pair_affinity_scores ?? {});
  const pairAffinityCount   = pairAffinityEntries.length;
  const pairAffinityAvg     = pairAffinityCount > 0
    ? pairAffinityEntries.reduce((sum, [, v]) => sum + Number(v), 0) / pairAffinityCount : 0;
  const pairAffinityMax     = pairAffinityCount > 0
    ? Math.max(...pairAffinityEntries.map(([, v]) => Number(v))) : 0;

  const radarData = [
    { metric: "Quality",     value: Number(signals?.dmc_quality_score) || 0 },
    { metric: "Acceptance",  value: (Number(signals?.acceptance_rate) || 0) * 100 },
    { metric: "Win Rate",    value: (Number(signals?.lead_win_rate) || 0) * 100 },
    { metric: "Lv→Response", value: (Number(signals?.lead_view_to_response_rate) || 0) * 100 },
    { metric: "Quote Rate",  value: (Number(signals?.quote_rate) || 0) * 100 },
    { metric: "V→Lead",      value: Math.min(100, (Number(signals?.view_to_lead_rate) || 0) * 200) },
    { metric: "Followers",   value: Math.min(100, (Number(signals?.follower_count) || 0) * 2) },
    { metric: "Deal Views",  value: Math.min(100, (Number(signals?.total_deal_views) || 0) / 5) },
  ];

  const cap = (v: number) => Math.min(100, v);
  const perfBar = [
    { name: "Win Rate",       pct: cap((Number(signals?.lead_win_rate) || 0) * 100) },
    { name: "Acceptance",     pct: cap((Number(signals?.acceptance_rate) || 0) * 100) },
    { name: "Quote Rate",     pct: cap((Number(signals?.quote_rate) || 0) * 100) },
    { name: "Lead Response",  pct: cap((Number(signals?.lead_view_to_response_rate) || 0) * 100) },
    { name: "View→Lead",      pct: cap((Number(signals?.view_to_lead_rate) || 0) * 100) },
    { name: "Junk Rate",      pct: cap((Number(signals?.junk_rate) || 0) * 100) },
  ];

  const B_COLORS = ["#f59e0b","#f97316","#ef4444","#ec4899","#8b5cf6","#6366f1","#06b6d4","#10b981","#84cc16","#a78bfa"];

  // Seller `history` and buyer `bHistory` are pushed on the same setInterval tick,
  // so they share timestamps and can be zipped by index into one chart's data.
  const combinedTickHistory = history.map((h, i) => ({
    label: h.label, t: h.t,
    sScore: h.score, sOpenRate: h.openRate, sConv: h.conv, sResp: h.resp,
    bScore: bHistory[i]?.score, bOpenRate: bHistory[i]?.openRate,
    bConv: bHistory[i]?.conv, bResp: bHistory[i]?.resp,
  }));

  // Buyer-as-seller derived data (full set — mirrors BuyerPanel)
  const bDests = Object.entries(buyerSigs?.interest_graph?.destinations ?? {})
    .sort((a, b) => b[1] - a[1]).slice(0, 8).map(([name, value]) => ({ name, value }));
  const bCats = Object.entries(buyerSigs?.interest_graph?.categories ?? {})
    .sort((a, b) => b[1] - a[1]).slice(0, 8).map(([name, value]) => ({ name, value }));
  const bTags = Object.entries(buyerSigs?.interest_graph?.tags ?? {})
    .sort((a, b) => b[1] - a[1]).slice(0, 8).map(([name, value]) => ({ name, value }));
  const bActivities = Object.entries(buyerSigs?.interest_graph?.activities ?? {})
    .sort((a, b) => b[1] - a[1]).slice(0, 8).map(([name, value]) => ({ name, value }));
  const bDmcAffinityEntries = Object.entries(buyerSigs?.dmc_affinity_scores ?? {});
  const bDmcAffinityCount   = bDmcAffinityEntries.length;
  const bDmcAffinityAvg     = bDmcAffinityCount > 0
    ? bDmcAffinityEntries.reduce((sum, [, v]) => sum + Number(v), 0) / bDmcAffinityCount : 0;
  const bDmcAffinityMax     = bDmcAffinityCount > 0
    ? Math.max(...bDmcAffinityEntries.map(([, v]) => Number(v))) : 0;
  const bAiCreatedTotal =
    (buyerSigs?.ai_itinerary_created_count ?? 0) + (buyerSigs?.ai_video_created_count ?? 0) +
    (buyerSigs?.ai_flyer_created_count ?? 0)     + (buyerSigs?.ai_slides_created_count ?? 0);
  const bAiEngagementTotal =
    (buyerSigs?.ai_itinerary_count ?? 0)        + (buyerSigs?.ai_video_count ?? 0) +
    (buyerSigs?.ai_slides_count ?? 0)           + (buyerSigs?.flyer_downloads ?? 0) +
    (buyerSigs?.ai_itinerary_shared_count ?? 0) + (buyerSigs?.video_shared_count ?? 0) +
    (buyerSigs?.ai_slides_shared_count ?? 0)    + (buyerSigs?.flyer_shared_count ?? 0) +
    (buyerSigs?.flyer_regenerated_count ?? 0);
  const bSeasonal = Object.entries(buyerSigs?.seasonal_patterns ?? {})
    .map(([name, value]) => ({ name: name.slice(0, 3).toUpperCase(), value: Math.round(value * 100) }));
  const bEngagementRadar = [
    { metric: "Conv",       value: (Number(buyerSigs?.conversion_rate) || 0) * 100 },
    { metric: "Repeat",     value: (Number(buyerSigs?.repeat_booking_rate) || 0) * 100 },
    { metric: "Scroll",     value: Math.min(100, (Number(buyerSigs?.scroll_engagement) || 0) * 100) },
    { metric: "Features",   value: (Number(buyerSigs?.feature_adoption) || 0) * 100 },
    { metric: "Notif Open", value: (Number(buyerSigs?.notification_open_rate) || 0) * 100 },
    { metric: "AI Media",   value: Math.min(100, (Number(buyerSigs?.total_ai_media_generated) || 0) * 10) },
  ];
  const bEngagementBar = [
    { name: "Conversion",  pct: (Number(buyerSigs?.conversion_rate) || 0) * 100 },
    { name: "Repeat Book", pct: (Number(buyerSigs?.repeat_booking_rate) || 0) * 100 },
    { name: "Notif Open",  pct: (Number(buyerSigs?.notification_open_rate) || 0) * 100 },
    { name: "Feature Use", pct: (Number(buyerSigs?.feature_adoption) || 0) * 100 },
    { name: "Junk Rate",   pct: (Number(buyerSigs?.junk_rate) || 0) * 100 },
  ];
  const bRecBar = buyerRecs.slice(0, 8).map((r) => ({ name: dealName(r), score: Number(r.score) || 0 }));

  // Welcome screen preview for seller-as-buyer — fetched from /rag/chips/admin/preview
  const [bPreviewData, setBPreviewData] = useState<{ welcome_title: string; welcome_sub: string; chips: string[]; error?: string } | null>(null);
  useEffect(() => {
    setBPreviewData(null);
    const firstName = user.name?.split(" ")[0] || "";
    apiClient.get<{ welcome_title: string; welcome_sub: string; chips: string[] }>(
      `/rag/chips/admin/preview?user_id=${user.id}&route=deals&limit=3&first_name=${encodeURIComponent(firstName)}&role=${encodeURIComponent(user.role)}&now_hour=${new Date().getHours()}`,
      { headers: tok() }
    ).then(res => setBPreviewData(res.data)).catch((e: any) => {
      const msg = e?.response?.data?.detail || e?.message || "preview fetch failed";
      setBPreviewData({ welcome_title: "", welcome_sub: "", chips: [], error: msg });
    });
  }, [user.id]);

  if (computing && !signals) return (
    <div className="flex flex-col items-center justify-center h-48 gap-3">
      <Spin sz={6} /><p className="text-gray-500 text-sm">Computing signals…</p>
    </div>
  );
  if (!signals) return (
    <div className="text-center py-16 space-y-3">
      {error
        ? <p className="text-red-400 text-sm font-mono bg-red-950/40 border border-red-800/40 rounded-lg px-4 py-3 max-w-md mx-auto">{error}</p>
        : <p className="text-gray-500 text-sm">No signals yet</p>
      }
      <button onClick={() => refresh(true)} className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs rounded-lg">Compute Now</button>
    </div>
  );

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400 font-bold text-lg flex-shrink-0">
          {user.name[0]?.toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-semibold">{user.name}</p>
          <p className="text-gray-400 text-xs truncate">{user.email}</p>
          {user.created_at && <p className="text-gray-600 text-xs mt-0.5">Member since {ago(user.created_at)}</p>}
        </div>
        {signals.dormancy_type && (
          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${
            signals.dormancy_type === "absolute" ? "bg-red-900/40 text-red-400 border border-red-800/40"
              : "bg-yellow-900/40 text-yellow-400 border border-yellow-800/40"
          }`}>{signals.dormancy_type.toUpperCase()}</span>
        )}
        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${TIER_COLOR[tier] ?? TIER_COLOR.free}`}>
          {tier.toUpperCase()}
        </span>
        <div className="flex items-center gap-1.5 text-xs text-gray-600 flex-shrink-0">
          {fetching || computing ? <Spin sz={3} /> : <Pulse />}
          {computing ? "computing…" : lastAt ? ago(lastAt.toISOString()) : "—"}
        </div>
        <button onClick={() => refresh(true)} title="Recompute"
          className="p-1.5 rounded-lg hover:bg-gray-700 text-gray-500 hover:text-amber-400 transition-colors flex-shrink-0">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {/* Seller Analytics / Live Feed tab bar */}
      <div className="flex gap-1 p-1 bg-gray-800/60 rounded-xl border border-gray-700/50">
        {(["seller", "feed"] as const).map((t) => (
          <button key={t} onClick={() => setSellerTab(t)}
            className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-all ${
              sellerTab === t
                ? t === "seller"
                  ? "bg-amber-500/20 text-amber-300 border border-amber-500/30"
                  : "bg-violet-500/20 text-violet-300 border border-violet-500/30"
                : "text-gray-500 hover:text-gray-300"
            }`}>
            {t === "seller" ? "Seller Analytics" : "Live Feed"}
          </button>
        ))}
      </div>

      {sellerTab === "seller" && (<>

      {/* DMC Quality Score Breakdown */}
      {(() => {
        const rt       = Number(signals.response_time_median_hours) || 999;
        const speedPts = parseFloat((30.0 * Math.exp(-rt / 4.0)).toFixed(2));
        const jr       = Number(signals.junk_rate) || 0;
        const junkPen  = parseFloat(Math.min(40.0, Math.pow(jr, 0.6) * 40.0).toFixed(2));
        const dmcQFields: QScoreField[] = [
          { label: "Acceptance Rate",          maxPts:  40, currentPts: (Number(signals.acceptance_rate) || 0) * 40,   rawValue: pct(signals.acceptance_rate),                                                                 window: "last 90d"  },
          { label: "Response Speed",           maxPts:  30, currentPts: speedPts,                                      rawValue: signals.response_time_median_hours != null ? `${signals.response_time_median_hours.toFixed(1)}h median` : "—", window: "last 30d · 30×e^(−t/4)" },
          { label: "Quote Rate",               maxPts:  30, currentPts: (Number(signals.quote_rate) || 0) * 30,        rawValue: pct(signals.quote_rate),                                                                      window: "last 90d"  },
          { label: "Junk Penalty",             maxPts: -40, currentPts: -junkPen,                                      rawValue: pct(signals.junk_rate),                                                                       window: "last 90d · min(40, jr^0.6×40)" },
        ];
        return <ScoreBreakdownCard title="DMC Quality Score" totalScore={Number(signals.dmc_quality_score) || 0} fields={dmcQFields} accent="#f59e0b" />;
      })()}

      {/* Buyer Quality Score Breakdown (seller acting as buyer) */}
      {buyerSigs && (() => {
        const bqScore = Number(buyerSigs.buyer_quality_score) || 0;
        const bqFields: QScoreField[] = [
          { label: "Conversion Rate",     maxPts:  60, currentPts: (Number(buyerSigs.conversion_rate) || 0) * 60,  rawValue: pct(buyerSigs.conversion_rate),  window: "last 90d"  },
          { label: "Repeat Booking",      maxPts:  25, currentPts: (Number(buyerSigs.repeat_booking_rate) || 0) * 25, rawValue: pct(buyerSigs.repeat_booking_rate), window: "last 180d" },
          { label: "Junk Rate (penalty)", maxPts: -15, currentPts: -(Number(buyerSigs.junk_rate) || 0) * 15,       rawValue: pct(buyerSigs.junk_rate),         window: "last 90d"  },
        ];
        return <ScoreBreakdownCard title="Buyer Quality Score (as Buyer)" totalScore={bqScore} fields={bqFields} accent="#6366f1" />;
      })()}

      {/* Formula A + B + C Weights */}
      <div className="grid grid-cols-3 gap-3">
        <FormulaWeightsCard
          title="Formula A — Deal Score Weights"
          subtitle="deal scoring · 0–100"
          accent="#6366f1"
          fields={[
            { label: "Relevance",             weight: 0.30, description: "cosine similarity × entity match (geo, budget, pax)" },
            { label: "Quality",               weight: 0.20, description: "DMC quality score ÷ 100" },
            { label: "Affinity",              weight: 0.15, description: "dmc_affinity_scores[dmc_id] · decayed half-life 90d" },
            { label: "Freshness",             weight: 0.10, description: "deal age · exp decay half-life 30d" },
            { label: "Conversion Likelihood", weight: 0.10, description: "heuristic or XGBoost · P(buyer books)" },
            { label: "Intent Match",          weight: 0.10, description: "query intent ↔ deal vertical · 16 classes" },
            { label: "Diversity Need",        weight: 0.05, description: "penalty if same DMC ≥ 3× in results" },
          ]}
        />
        <FormulaWeightsCard
          title="Formula B — Lead Score"
          subtitle="lead inbox · 0–100"
          accent="#f59e0b"
          fields={[
            { label: "Conversion Likelihood", weight: 0.30, description: "P(DMC closes lead) · heuristic or XGBoost" },
            { label: "Match Quality",         weight: 0.20, description: "geo + vertical + budget + pax alignment" },
            { label: "Urgency",               weight: 0.15, description: "≤7d=1.0 · ≤14d=0.8 · ≤30d=0.5 · else=0" },
            { label: "Buyer Quality",         weight: 0.10, description: "buyer_quality_score ÷ 100" },
            { label: "Affinity",              weight: 0.10, description: "pair closures ÷ total enquiries · this pair" },
            { label: "Quota Pressure",        weight: 0.10, description: "quota_utilization < 50% → boost" },
            { label: "Profit Potential",      weight: 0.05, description: "budget_max × margin / ₹30,000" },
          ]}
        />
        <FormulaWeightsCard
          title="Formula C — Deal Suggestions"
          subtitle="demand gap · weekly · last 30d"
          accent="#06b6d4"
          fields={[
            { label: "Demand Gap",           weight: 0.35, description: "query_vol × (1−match_rate) ÷ competitors" },
            { label: "Capability Match",     weight: 0.20, description: "DMC corridor + vertical vs gap" },
            { label: "Competition Headroom", weight: 0.15, description: "1 − min(competitor_count / 10, 1)" },
            { label: "Margin Potential",     weight: 0.10, description: "estimated margin for dest + vertical" },
            { label: "Seasonality Align",    weight: 0.10, description: "peak season proximity for destination" },
            { label: "Refresh Need",         weight: 0.10, description: "days since last deal update in category" },
          ]}
        />
      </div>

      {/* Stat pills — row 1: core performance */}
      <div className="grid grid-cols-4 gap-2">
        <StatPill label="DMC Quality"       value={(signals.dmc_quality_score ?? 0).toFixed(1)}       accent="amber"   context="/ 100 · daily compute"             tooltip="Composite seller quality score (0–100). Weighted: acceptance rate, win rate, response time, portfolio completeness. Primary input for Formula B lead routing — higher score = more leads routed." />
        <StatPill label="Acceptance Rate"   value={pct(signals.acceptance_rate)}                      accent="emerald" context="last 90d · leads accepted"         tooltip="% of routed leads the seller accepted in last 90d. Low acceptance rate signals the seller is too selective or quota-limited." />
        <StatPill label="Win Rate"          value={pct(signals.lead_win_rate)}                        accent="violet"  context="last 90d · leads closed"            tooltip="% of accepted leads that resulted in a confirmed booking. The core revenue conversion metric for the seller." />
        <StatPill label="Quote Rate"        value={pct(signals.quote_rate)}                           accent="cyan"    context="last 90d · quotes sent"             tooltip="% of received leads for which the seller sent a formal quote. Low quote rate = slow or unresponsive seller." />
      </div>

      {/* Stat pills — row 2: lead metrics */}
      <div className="grid grid-cols-4 gap-2">
        <StatPill label="Total Leads"       value={signals.total_leads_count ?? 0}                    accent="amber"   context="all time · received"               tooltip="Total leads received by this seller across all time. Raw volume — not filtered by status." />
        <StatPill label="Active Leads"      value={signals.active_lead_count ?? 0}                    accent="emerald" context="currently open"                   tooltip="Leads currently in an open/active state — not yet won, lost, or expired. High count may indicate pipeline overload." />
        <StatPill label="View → Lead"       value={`${Math.min(100, (Number(signals.view_to_lead_rate) || 0) * 100).toFixed(1)}%`} accent="indigo" context="deal views → lead conversion" tooltip="% of deal page views that converted into an actual lead. Measures how compelling the seller's deal listings are." />
        <StatPill label="Lead View → Resp"  value={pct(signals.lead_view_to_response_rate)}           accent="cyan"    context="last 30d · response after view"   tooltip="% of leads the seller viewed and then responded to in last 30d. Low rate = seller is ghosting leads after viewing them." />
      </div>

      {/* Stat pills — row 3: revenue + price */}
      <div className="grid grid-cols-4 gap-2">
        <StatPill label="Deal Likes"        value={signals.total_deal_likes ?? 0}                     accent="pink"    context="buyer likes on deals"             tooltip="Total buyer likes on this seller's deals in last 180d." />
        <StatPill label="Avg Deal Price"    value={fmtInr(signals.avg_deal_price)}                    accent="amber"   context="last 90d · deal avg"              tooltip="Average price of deals closed in last 90d. Used in pricing nudge engine — if avg is below demand gap threshold, seller gets a price-up suggestion." />
        <StatPill label="Response Time"     value={signals.response_time_median_hours != null ? `${signals.response_time_median_hours.toFixed(1)}h` : "—"} accent="pink" context="last 30d · median · <2h=+20pts" tooltip="Median time (hours) between lead received and first seller response in last 30d. Sub-2h adds +20pts to DMC Quality score." />
        <StatPill label="Quota"             value={signals.quota_status}                              accent="emerald" context="this week's lead quota"            tooltip="Seller's current weekly lead quota status. Quota-full sellers are deprioritized in routing — Formula B skips them when at capacity." />
      </div>

      {/* Stat pills — row 4: visibility */}
      <div className="grid grid-cols-4 gap-2">
        <StatPill label="Profile Views"     value={signals.profile_view_count ?? 0}                   accent="sky"    context="last 30d · total"                 tooltip="Buyer views of this seller's profile page in last 30d. Strong demand-side interest signal — high views with low leads = deal listing problem." />
        <StatPill label="Deal Views"        value={signals.total_deal_views ?? 0}                     accent="indigo" context="last 30d · across all deals"      tooltip="Total views across all of the seller's active deal listings in last 30d. Feeds into View → Lead conversion rate." />
        <StatPill label="Followers"         value={signals.follower_count ?? 0}                       accent="purple" context="total · no decay"                  tooltip="Buyers who have followed this seller. No time decay — represents long-term brand equity." />
        <StatPill label="Saves"             value={signals.save_count ?? 0}                           accent="amber"  context="last 30d · deal saves by buyers"   tooltip="Buyers who saved/bookmarked this seller's deals in last 30d. High saves with low enquiries = price or CTA friction." />
      </div>

      {/* Stat pills — row 5: content + contact signals */}
      <div className="grid grid-cols-4 gap-2">
        <StatPill label="Seller RAG"        value={signals.seller_rag_query_count ?? 0}               accent="violet"  context="seller's own AI searches"         tooltip="AI search queries made by the seller themselves in last 30d." />
        <StatPill label="Flyer Downloads"   value={signals.flyer_download_count ?? 0}                 accent="sky"     context="last 30d · total"                 tooltip="Flyer/brochure downloads for this seller's deals in last 30d. Offline engagement proxy — buyers downloading are closer to booking." />
        <StatPill label="Cross-sell Enq."   value={signals.cross_sell_enquiry_count ?? 0}             accent="emerald" context="last 30d · cross-sell leads"       tooltip="Enquiries where a buyer was cross-sold from another seller's deal to this seller in last 30d. Powered by Formula A demand-gap routing." />
        <StatPill label="Contact Clicks"    value={signals.contact_click_count ?? 0}                  accent="cyan"    context="buyer 'Contact DMC' taps"          tooltip="Times buyers clicked 'Contact DMC' on this seller's deals. High-intent signal." />
      </div>

      {/* Stat pills — row 6: engagement signals */}
      <div className="grid grid-cols-2 gap-2">
        <StatPill label="Days Since Update" value={signals.days_since_last_deal_update != null && signals.days_since_last_deal_update < 999 ? `${signals.days_since_last_deal_update}d` : "—"} accent={signals.days_since_last_deal_update != null && signals.days_since_last_deal_update < 999 && signals.days_since_last_deal_update > 30 ? "red" : "gray"} context="> 30d = stale · pricing nudge risk" tooltip="Days since this seller last updated any deal. >30d triggers a pricing nudge in Formula C. Stale listings get downranked in Formula B." />
      </div>

      {/* Stat pills — row 7: deal catalogue size */}
      <div className="grid grid-cols-4 gap-2">
        <StatPill label="Active Deals"      value={signals.active_deals_count ?? 0}          accent="emerald" context="currently live listings"           tooltip="Deals live in the feed right now. Formula B's new-seller warmup boost requires at least one — a zero-inventory seller has nothing to sell a routed lead, so the boost is withheld." />
        <StatPill label="Total Uploaded"    value={signals.total_deals_uploaded_count ?? 0}  accent="sky"     context="lifetime · incl. rejected/pending" tooltip="Lifetime deal uploads, including rejected and pending ones. A large gap against Active Deals means most submissions are not passing review." />
      </div>

      {/* Last active + junk rate */}
      <Card>
        <SH>Seller Activity Status</SH>
        <div className="flex flex-wrap gap-4 text-xs">
          <span className="text-gray-500">Last active: <span className="text-white">{ago(signals.last_active_at)}</span></span>
          <span className="text-gray-500">Junk rate: <span className={Number(signals.junk_rate) > 0.1 ? "text-red-400" : "text-emerald-400"}>{pct(signals.junk_rate)}</span></span>
        </div>
      </Card>

      {/* Seller Engagement Radar */}
      <Card>
        <SH>Seller Performance Radar</SH>
        <SellerRadar data={radarData} />
      </Card>

      {/* Performance breakdown bar */}
      <Card>
        <SH right="% metric">Performance Breakdown</SH>
        <VBar data={perfBar} xKey="name" yKey="pct" color="#f59e0b" height={130} />
      </Card>

      {/* Primary Corridors + Verticals */}
      <div className="grid grid-cols-2 gap-3">
        {(signals.primary_corridors ?? []).length > 0 && (
          <Card>
            <SH right={`${(signals.primary_corridors ?? []).length}`}>Primary Corridors</SH>
            <div className="flex flex-wrap gap-1.5">
              {(signals.primary_corridors ?? []).map((c) => (
                <span key={c} className="px-2.5 py-1 bg-amber-900/30 text-amber-300 text-xs rounded-full border border-amber-800/40">{c}</span>
              ))}
            </div>
          </Card>
        )}
        {(signals.primary_verticals ?? []).length > 0 && (
          <Card>
            <SH right={`${(signals.primary_verticals ?? []).length}`}>Primary Verticals</SH>
            <div className="flex flex-wrap gap-1.5">
              {(signals.primary_verticals ?? []).map((v) => (
                <span key={v} className="px-2.5 py-1 bg-orange-900/30 text-orange-300 text-xs rounded-full border border-orange-800/40">{v}</span>
              ))}
            </div>
          </Card>
        )}
      </div>

      {/* Views by Corridor */}
      {viewsByCorridor.length > 0 && (
        <Card>
          <SH right="deal view distribution">Views by Corridor</SH>
          <HorizBar data={viewsByCorridor} nameKey="name" valueKey="value" color="#f59e0b" />
        </Card>
      )}

      {/* Pair Affinity — aggregate (IDs not resolvable, show stats) */}
      {pairAffinityCount > 0 && (
        <div className="grid grid-cols-3 gap-2">
          <StatPill label="Buyers Engaged"  value={pairAffinityCount}               accent="cyan" />
          <StatPill label="Avg Affinity"    value={pairAffinityAvg.toFixed(3)}      accent="indigo" />
          <StatPill label="Top Affinity"    value={pairAffinityMax.toFixed(3)}      accent="emerald" />
        </div>
      )}

      {/* Lead Inbox — Formula B */}
      {leadBar.length > 0 && (() => {
        const maxBScore = Math.max(...leadBar.map((l) => l.score), 1);
        return (
          <Card>
            <SH right={`${leadBar.length} leads`}>Lead Inbox — Formula B Scores</SH>
            <div className="space-y-2">
              {leadBar.map((l, i) => {
                const normPct = Math.round((l.score / maxBScore) * 100);
                return (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className="text-gray-300 w-32 truncate flex-shrink-0">{l.name}</span>
                    <div className="flex-1 bg-gray-800 rounded-full h-2">
                      <div className="h-2 rounded-full transition-all duration-700"
                        style={{ width: `${normPct}%`, background: B_COLORS[i % B_COLORS.length] }} />
                    </div>
                    <span className="font-mono text-amber-300 w-10 text-right flex-shrink-0">{normPct}%</span>
                    <span className="text-cyan-400 font-mono w-10 text-right flex-shrink-0">{pct(l.conv)}</span>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-gray-700 mt-2 text-right">relative B-score % · conv%</p>
          </Card>
        );
      })()}
      {leads.length === 0 && (
        <Card>
          <SH>Lead Inbox — Formula B</SH>
          <p className="text-gray-600 text-xs text-center py-4">No leads routed yet</p>
        </Card>
      )}

      {/* Deal Suggestions — Formula C */}
      {suggBar.length > 0 && (
        <Card>
          <SH right={`${suggBar.length} suggestions`}>Deal Suggestions — Formula C (Demand Gaps)</SH>
          <VBar data={suggBar} xKey="name" yKey="demand" color="#06b6d4" height={130} />
        </Card>
      )}

      {/* Top Lead hero — Formula B #1 priority */}
      {leads.length > 0 && (() => {
        const top = leads[0];
        const maxB = Math.max(...leads.map((l) => Number(l.formula_b_score) || 0), 1);
        const normPct = Math.round((Number(top.formula_b_score) / maxB) * 100);
        return (
          <Card className="border-amber-500/30 bg-amber-950/20">
            <SH right="Formula B · #1 priority">Top Lead to Action</SH>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0 text-lg">🎯</div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold text-sm">{leadLabel(top)}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  <div className="flex-1 bg-gray-800 rounded-full h-1.5">
                    <div className="h-1.5 rounded-full bg-amber-500 transition-all duration-700" style={{ width: `${normPct}%` }} />
                  </div>
                  <span className="text-amber-300 font-mono text-xs flex-shrink-0">{normPct}% B-score</span>
                </div>
                <div className="flex gap-3 mt-1.5 text-xs flex-wrap">
                  {top.tier && <span className="text-gray-400">Tier: <span className="text-amber-300">{top.tier}</span></span>}
                  {top.lead?.status && <span className="text-gray-400">Status: <span className="text-white">{top.lead.status}</span></span>}
                  {top.lead?.budget && <span className="text-gray-400">Budget: <span className="text-emerald-300">{top.lead.budget}</span></span>}
                  {top.lead?.pax != null && <span className="text-gray-400">Pax: <span className="text-sky-300">{top.lead.pax}</span></span>}
                  {top.lead?.created_at && <span className="text-gray-600">{ago(top.lead.created_at)}</span>}
                </div>
              </div>
            </div>
          </Card>
        );
      })()}

      {/* Live charts — seller + buyer signals, combined into one multi-line chart */}
      <CombinedSignalChart data={combinedTickHistory} lines={SELLER_BUYER_COMBINED_LINES} title="Seller + Buyer Signals — Combined Live View" />

      {/* AI Media — buyer downloads from seller's content */}
      <SectionDivider label="AI Media (Buyer Downloads)" />
      <div className="grid grid-cols-4 gap-2">
        <StatPill label="AI Itinerary"      value={signals.ai_itinerary_count ?? 0}          accent="violet"  context="downloaded by buyers" />
        <StatPill label="AI Video"          value={signals.ai_video_count ?? 0}              accent="pink"    context="downloaded by buyers" />
        <StatPill label="AI Slides"         value={signals.ai_slides_count ?? 0}             accent="sky"     context="downloaded by buyers" />
        <StatPill label="Content Shares"    value={signals.buyer_content_share_count ?? 0}   accent="emerald" context="shared by buyers" />
      </div>

      {/* AI Content generated/downloaded/shared by this seller acting as a buyer,
          plus their fresh-RAG chat usage — grouped right next to the seller-side
          AI Media block above since both are "AI content" concerns. */}
      {buyerSigs && (<>
        <SectionDivider label="AI Content (Buyer-Side)" />

        <Card className="border-indigo-900/30">
          <SH right={`${buyerSigs.total_ai_media_generated ?? 0} generated`}>
            Created
            <InfoTip text="Successful AI generations, split by media type. These count creations only — media can be generated and never downloaded or shared, which the Engagement group tracks separately." />
          </SH>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <StatPill label="Itineraries" value={buyerSigs.ai_itinerary_created_count ?? 0} accent="violet" context="generated" tooltip="AI itineraries generated. Highest-weight AI intent signal (weight 8) in the interest graph — an itinerary means active planning of a specific trip." />
            <StatPill label="Videos"      value={buyerSigs.ai_video_created_count ?? 0}     accent="pink"   context="generated" tooltip="AI promo videos generated. Feeds the interest graph at weight 5." />
            <StatPill label="Flyers"      value={buyerSigs.ai_flyer_created_count ?? 0}     accent="indigo" context="generated" tooltip="AI marketing flyers generated. Feeds the interest graph at weight 6." />
            <StatPill label="Slides"      value={buyerSigs.ai_slides_created_count ?? 0}    accent="sky"    context="generated" tooltip="AI slide decks generated. Feeds the interest graph at weight 4." />
          </div>
          {bAiCreatedTotal !== (buyerSigs.total_ai_media_generated ?? 0) && (
            <p className="text-[10px] text-gray-600 mt-2">
              {bAiCreatedTotal} of {buyerSigs.total_ai_media_generated ?? 0} generations carry a known media type
            </p>
          )}
        </Card>

        <Card className="border-indigo-900/30">
          <SH right={`${bAiEngagementTotal} actions`}>
            Engagement
            <InfoTip text="What happened to the generated media afterwards. Downloads and shares are separate events from generation." />
          </SH>
          <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Downloaded</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <StatPill label="Itineraries" value={buyerSigs.ai_itinerary_count ?? 0} accent="violet" context="downloaded" />
            <StatPill label="Videos"      value={buyerSigs.ai_video_count ?? 0}     accent="pink"   context="downloaded" />
            <StatPill label="Flyers"      value={buyerSigs.flyer_downloads ?? 0}    accent="indigo" context="downloaded" />
            <StatPill label="Slides"      value={buyerSigs.ai_slides_count ?? 0}    accent="sky"    context="downloaded" />
          </div>
          <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider mt-3 mb-1.5">Shared</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <StatPill label="Itineraries" value={buyerSigs.ai_itinerary_shared_count ?? 0} accent="violet" context="shared" />
            <StatPill label="Videos"      value={buyerSigs.video_shared_count ?? 0}        accent="pink"   context="shared" />
            <StatPill label="Flyers"      value={buyerSigs.flyer_shared_count ?? 0}        accent="indigo" context="shared" />
            <StatPill label="Slides"      value={buyerSigs.ai_slides_shared_count ?? 0}    accent="sky"    context="shared" />
          </div>
          <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider mt-3 mb-1.5">Regenerated</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <StatPill label="Flyers" value={buyerSigs.flyer_regenerated_count ?? 0} accent="gray" context="regenerated" tooltip="Times flyer generation was re-run. Repeated regeneration on the same deal can indicate the first output missed the mark." />
          </div>
        </Card>

        {/* RAG query + market research — grouped with AI content per the same "AI usage" concern */}
        <SectionDivider label="RAG Query &amp; Market Research (Buyer-Side)" />
        <Card className="border-indigo-900/30">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <StatPill label="Chat Queries"    value={buyerSigs.total_rag_queries ?? 0}            accent="violet" context="total chat turns"        tooltip="Total AI chat/search turns. Each turn is a destination or deal-intent signal fed into the interest graph." />
            <StatPill label="Market Research" value={buyerSigs.market_research_query_count ?? 0}  accent="cyan"   context="of which market-research" tooltip="Chat turns issued from the Market Research tab. A subset of Chat Queries, not an additional count." />
          </div>
        </Card>
      </>)}

      {/* Seller's own activity */}
      <SectionDivider label="Seller Activity" />
      <div className="grid grid-cols-2 gap-2">
        <StatPill label="Sessions"          value={signals.seller_session_count ?? 0}         accent="indigo"  context="seller's own logins" />
        <StatPill label="AI Generated"      value={signals.ai_media_generated_count ?? 0}    accent="emerald" context="AI content created" />
      </div>
      <div className="grid grid-cols-4 gap-2">
        <StatPill label="Deals Created"     value={signals.deal_created_count ?? 0}           accent="emerald" context="new listings created" />
        <StatPill label="Deals Updated"     value={signals.deal_updated_count ?? 0}           accent="sky"     context="listings updated" />
        <StatPill label="Deals Deleted"     value={signals.deal_deleted_count ?? 0}           accent="red"     context="listings removed" />
        <StatPill label="Notif Open Rate"   value={signals.seller_notification_open_rate != null ? pct(signals.seller_notification_open_rate) : "—"} accent="cyan" context="seller's notif opens" />
      </div>
      {buyerSigs && (
        <div className="grid grid-cols-4 gap-2">
          <StatPill label="Notif Open Rate (as Buyer)" value={pct(buyerSigs.notification_open_rate)} accent="sky" context="last 30d · open ÷ sent" />
        </div>
      )}

      {/* Raw buyer engagement counts */}
      <SectionDivider label="Raw Counts" />
      <div className="grid grid-cols-4 gap-2">
        <StatPill label="Enquiries Recv."   value={signals.total_enquiries_received ?? 0}    accent="amber"   context="enquiries received" />
        <StatPill label="DM Threads"        value={signals.total_dm_threads ?? 0}            accent="emerald" context="conversations" />
        <StatPill label="Enquiry Drafts"    value={signals.total_enquiry_drafts ?? 0}        accent="gray"    context="pre-enquiry drafts" />
        <StatPill label="Quotes Received"   value={signals.total_quotes_received ?? 0}       accent="violet"  context="formal quotes" />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <StatPill label="Deal Downloads"    value={signals.total_deal_downloads ?? 0}        accent="sky"     context="deal PDFs downloaded" />
        <StatPill label="News Views"        value={signals.news_view_count ?? 0}             accent="cyan"    context="news articles viewed" />
        <StatPill label="News Likes"        value={signals.news_like_count ?? 0}             accent="pink"    context="news liked" />
      </div>

      {/* Raw counts, buyer-side — same metrics as above but for this seller's own
          activity when acting as a buyer, grouped right next to Raw Counts. */}
      {buyerSigs && (<>
        <SectionDivider label="Raw Counts (Buyer-Side)" />
        <div className="grid grid-cols-4 gap-2">
          <StatPill label="Enquiries Sent"    value={buyerSigs.total_enquiries_sent ?? 0}      accent="amber"   context="total enquiries" />
          <StatPill label="DM Threads (as Buyer)" value={buyerSigs.total_dm_threads ?? 0}      accent="emerald" context="conversations opened" />
          <StatPill label="Quote Requests"    value={buyerSigs.total_quotes_requested ?? 0}    accent="violet"  context="formal quotes" />
          <StatPill label="Deal Views (as Buyer)" value={buyerSigs.total_deal_views ?? 0}      accent="indigo"  context="deals viewed" />
        </div>
        <div className="grid grid-cols-4 gap-2">
          <StatPill label="Deal Likes (as Buyer)" value={buyerSigs.total_deal_likes ?? 0}      accent="pink"    context="deals liked" />
          <StatPill label="Deal Downloads (as Buyer)" value={buyerSigs.total_deal_downloads ?? 0} accent="sky"  context="deal PDFs" />
          <StatPill label="Enquiry Drafts (as Buyer)" value={buyerSigs.total_enquiry_drafts ?? 0} accent="gray" context="pre-enquiry drafts" />
          <StatPill label="News Views (as Buyer)" value={buyerSigs.total_news_views ?? 0}      accent="cyan"    context="news articles viewed" />
        </div>
        <div className="grid grid-cols-4 gap-2">
          <StatPill label="News Likes (as Buyer)" value={buyerSigs.total_news_likes ?? 0}      accent="pink"    context="news liked" />
        </div>
      </>)}

      {/* ── Buyer Persona Section (seller also acts as buyer) ────────────── */}
      {!buyerSigs && (
        <div className="text-center py-8">
          <p className="text-gray-500 text-sm">No buyer signals yet — click Compute Now</p>
        </div>
      )}
      {buyerSigs && (
        <>
          <SectionDivider label="Buyer-Side Activity" />

          {/* Identity + quality */}
          <div className="grid grid-cols-4 gap-2">
            <StatPill label="Buyer Quality"     value={(Number(buyerSigs.buyer_quality_score) || 0).toFixed(1)} accent="indigo"  context="/ 100 · daily compute" />
            <StatPill label="Budget Band"       value={buyerSigs.avg_budget_band}                               accent="cyan"    context="avg booking value band" />
            <StatPill label="Pax Band"          value={buyerSigs.avg_pax_band}                                  accent="purple"  context="typical group size band" />
            <StatPill label="Avg Booking"       value={fmtInr(buyerSigs.avg_booking_value)}                     accent="emerald" context="last 90d · booking avg" />
          </div>
          <div className="grid grid-cols-4 gap-2">
            <StatPill label="Group Size"        value={buyerSigs.typical_group_size?.toFixed(1)}                accent="sky"    context="last 90d · typical pax" />
            <StatPill label="Search / day"      value={(buyerSigs.search_frequency ?? 0).toFixed(2)}            accent="violet" context="last 30d · daily avg" />
            <StatPill label="Feature Adoption"  value={pct(buyerSigs.feature_adoption)}                         accent="amber"  context="/ 100% platform features" />
            <StatPill label="Signal Weight"     value={(buyerSigs.signal_absorption_weight ?? 1).toFixed(2)}    accent="pink"   context="1.0=self · 0.3=client mode" />
          </div>
          <div className="grid grid-cols-1 gap-2">
            <StatPill label="Sessions (as Buyer)" value={buyerSigs.session_count ?? 0}                          accent="indigo" context="last 30d · login sessions" />
          </div>
          <div className="grid grid-cols-4 gap-2">
            <StatPill label="Conv. Rate"        value={pct(buyerSigs.conversion_rate)}                          accent="amber"   context="last 90d · bookings ÷ quotes" />
            <StatPill label="Repeat Booking"    value={pct(buyerSigs.repeat_booking_rate)}                      accent="purple"  context="last 180d · loyalty signal" />
            <StatPill label="Junk Rate"         value={pct(buyerSigs.junk_rate)}                                accent="red"     context="last 90d · 0% ideal · −15pts" />
          </div>
          <div className="grid grid-cols-4 gap-2">
            <StatPill label="Contact Clicks"    value={buyerSigs.total_contact_clicks ?? 0}                     accent="emerald" context="last 90d · DMC contact taps" />
            <StatPill label="Leads Created"     value={buyerSigs.total_leads_created ?? 0}                      accent="amber"   context="last 90d · quote requests" />
            <StatPill label="Wishlist"          value={buyerSigs.wishlist_deal_ids?.length ?? 0}                 accent="amber"   context="saved deals · 60d active" />
            <StatPill label="Followed DMCs"     value={(buyerSigs.followed_dmc_ids ?? []).length}               accent="purple"  context="total · no decay" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <StatPill label="Avg Resp. Time"    value={buyerSigs.avg_response_time_hours != null ? `${buyerSigs.avg_response_time_hours.toFixed(1)}h` : "—"} accent="cyan" context="hrs to respond to lead" />
            <StatPill label="Dismissed Deals"   value={(buyerSigs.dismissed_deal_ids ?? []).length}              accent="gray"    context="last 7d · suppressed from recs" />
            <StatPill label="Corridor"          value={buyerSigs.primary_corridor}                               accent="emerald" context="top destination · last 12mo" />
          </div>

          {/* Stale / secondary corridors */}
          {(buyerSigs.profile_is_stale || (buyerSigs.secondary_corridors ?? []).length > 0) && (
            <Card className="border-indigo-900/30">
              <SH>Buyer Profile Status</SH>
              <div className="flex flex-wrap gap-3 text-xs">
                <span className="text-gray-500">Last active: <span className="text-white">{ago(buyerSigs.last_active_at)}</span></span>
                {buyerSigs.profile_is_stale && <span className="text-red-400">Stale since {ago(buyerSigs.stale_since)}</span>}
                {(buyerSigs.secondary_corridors ?? []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 w-full mt-1">
                    <span className="text-gray-500">Secondary corridors:</span>
                    {(buyerSigs.secondary_corridors ?? []).map((c) => (
                      <span key={c} className="px-2 py-0.5 bg-indigo-900/30 text-indigo-300 rounded-full border border-indigo-800/40 text-xs">{c}</span>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Engagement radar */}
          <Card className="border-indigo-900/30">
            <SH>Buyer Engagement Radar</SH>
            <EngagementRadar data={bEngagementRadar} />
          </Card>

          {/* Engagement bar */}
          <Card className="border-indigo-900/30">
            <SH right="% of activity">Buyer Engagement Breakdown</SH>
            <VBar data={bEngagementBar} xKey="name" yKey="pct" color="#6366f1" height={130} />
          </Card>

          {/* Seasonal patterns */}
          {bSeasonal.length > 0 && (
            <Card className="border-indigo-900/30">
              <SH right="travel seasonality">Buyer — Seasonal Activity Patterns</SH>
              <VBar data={bSeasonal} xKey="name" yKey="value" color="#06b6d4" height={110} />
            </Card>
          )}

          {/* Interest graph — all 4 dimensions */}
          <div className="grid grid-cols-2 gap-3">
            {bDests.length > 0 && (
              <Card className="border-indigo-900/30">
                <SH right={`${bDests.length}`}>Buyer — Top Destinations</SH>
                <HorizBar data={bDests} nameKey="name" valueKey="value" color="#6366f1" />
              </Card>
            )}
            {bCats.length > 0 && (
              <Card className="border-indigo-900/30">
                <SH right={`${bCats.length}`}>Buyer — Interest Categories</SH>
                <HorizBar data={bCats} nameKey="name" valueKey="value" color="#8b5cf6" />
              </Card>
            )}
            {bTags.length > 0 && (
              <Card className="border-indigo-900/30">
                <SH right={`${bTags.length}`}>Buyer — Interest Tags</SH>
                <HorizBar data={bTags} nameKey="name" valueKey="value" color="#06b6d4" />
              </Card>
            )}
            {bActivities.length > 0 && (
              <Card className="border-indigo-900/30">
                <SH right={`${bActivities.length}`}>Buyer — Activities</SH>
                <HorizBar data={bActivities} nameKey="name" valueKey="value" color="#10b981" />
              </Card>
            )}
          </div>

          {/* Session / Device */}
          <SectionDivider label="Sessions &amp; Device" />
          <div className="grid grid-cols-2 gap-2">
            <StatPill label="Sessions (7d)"     value={buyerSigs.session_frequency_7d ?? 0}      accent="sky"     context="last 7 days" />
            <StatPill label="Avg Session"       value={fmtMs(buyerSigs.avg_session_duration_ms)} accent="cyan"    context="active time" />
          </div>

          {/* Profile views (as buyer) — the one Raw Interactions field with no seller-side pair */}
          <div className="grid grid-cols-4 gap-2">
            <StatPill label="Profile Views (as Buyer)" value={buyerSigs.total_dmc_profile_views ?? 0}   accent="amber"   context="DMC profiles viewed" />
          </div>

          {/* DMC Affinity as buyer — aggregate (IDs not resolvable) */}
          {bDmcAffinityCount > 0 && (
            <div className="grid grid-cols-3 gap-2">
              <StatPill label="DMCs Engaged"  value={bDmcAffinityCount}           accent="amber" />
              <StatPill label="Avg Affinity"  value={bDmcAffinityAvg.toFixed(3)}  accent="indigo" />
              <StatPill label="Top Affinity"  value={bDmcAffinityMax.toFixed(3)}  accent="emerald" />
            </div>
          )}

          {/* Top Pick hero */}
          {buyerRecs.length > 0 && (
            <Card className="border-indigo-500/30 bg-indigo-950/20">
              <SH right="Formula A · #1">Top Recommendation</SH>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center flex-shrink-0 text-lg">🏆</div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm">{dealName(buyerRecs[0])}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <div className="flex-1 bg-gray-800 rounded-full h-1.5">
                      <div className="h-1.5 rounded-full bg-indigo-500 transition-all duration-700" style={{ width: `${Math.min(100, buyerRecs[0].score)}%` }} />
                    </div>
                    <span className="text-indigo-300 font-mono text-xs flex-shrink-0">{buyerRecs[0].score.toFixed(1)} / 100</span>
                  </div>
                  {(buyerRecs[0].reason_strings ?? []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {(buyerRecs[0].reason_strings ?? []).map((rs, i) => (
                        <span key={i} className="px-2 py-0.5 bg-indigo-900/40 text-indigo-300 text-xs rounded-full border border-indigo-800/40">{rs}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          )}

          {/* Formula A recs as buyer */}
          {bRecBar.length > 0 && (
            <Card className="border-indigo-900/30">
              <SH right={`${bRecBar.length} deals`}>Buyer — Formula A Deal Recommendations</SH>
              <div className="space-y-1.5">
                {bRecBar.map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className="text-gray-400 w-36 truncate flex-shrink-0">{r.name}</span>
                    <div className="flex-1 bg-gray-800 rounded-full h-2.5">
                      <div className="h-2.5 rounded-full transition-all duration-700"
                        style={{ width: `${Math.min(100, r.score)}%`, background: FORMULA_COLORS[i % FORMULA_COLORS.length] }} />
                    </div>
                    <span className="font-mono text-indigo-300 w-10 text-right flex-shrink-0">{r.score.toFixed(1)}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Formula A component breakdown for top rec */}
          {buyerRecs[0]?.components && Object.keys(buyerRecs[0].components).length > 0 && (
            <Card className="border-indigo-900/30">
              <SH right={dealName(buyerRecs[0])}>Formula A — Component Breakdown (Top Deal)</SH>
              <VBar
                data={Object.entries(buyerRecs[0].components).map(([k, v]) => ({
                  name: k.replace(/_/g, " "),
                  val: typeof v === "object" && v !== null
                    ? Math.round(Number((v as any).score ?? 0) * 100)
                    : Number(v) || 0,
                }))}
                xKey="name" yKey="val" color="#8b5cf6" height={130}
              />
            </Card>
          )}

          {/* Welcome screen preview — seller as buyer */}
          <Card className="border-indigo-900/30">
            <SH>Welcome Screen Preview (Buyer View)</SH>
            <p className="text-[11px] text-gray-500 -mt-1 mb-3">What this seller sees on RAG chat welcome screen when browsing deals</p>
            {!bPreviewData ? (
              <div className="text-center py-4 text-gray-600 text-xs">Loading preview…</div>
            ) : bPreviewData.error ? (
              <div className="text-center py-4 text-red-400 text-xs font-mono">{bPreviewData.error}</div>
            ) : bPreviewData.chips.length === 0 ? (
              <div className="text-center py-4 text-gray-600 text-xs">No buyer signals yet — user sees admin fallback chips</div>
            ) : (
              <div className="bg-gray-900/60 border border-indigo-800/30 rounded-xl p-4 space-y-3">
                <p className="text-white text-sm font-semibold text-center leading-snug">{bPreviewData.welcome_title}</p>
                <p className="text-gray-400 text-xs text-center leading-relaxed">{bPreviewData.welcome_sub}</p>
                <div className="flex flex-wrap gap-1.5 justify-center">
                  {bPreviewData.chips.map((chip) => (
                    <span key={chip} className="px-3 py-1 border border-indigo-700/40 rounded-full text-xs text-indigo-200 bg-indigo-900/20">{chip}</span>
                  ))}
                </div>
              </div>
            )}
          </Card>

        </>
      )}

      </>)}

      {/* ── Live Feed tab ── */}
      {sellerTab === "feed" && (<>

        {/* Lead Inbox — what seller actually sees */}
        <div className="space-y-2">
          <div className="flex items-center justify-between px-1">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Lead Inbox — Formula B ({leads.length})
            </p>
          </div>
          {leads.length === 0 && (
            <div className="text-center py-8 text-gray-600 text-sm">No leads yet</div>
          )}
          {leads.map((lead, i) => {
            const score = Number(lead.formula_b_score) || 0;
            const dests = Array.isArray(lead.lead?.destination)
              ? lead.lead!.destination as string[]
              : lead.lead?.destination ? [lead.lead.destination as string] : [];
            const STATUS_COLOR: Record<string, string> = {
              open:      "bg-emerald-900/40 text-emerald-300 border-emerald-700/40",
              won:       "bg-sky-900/40 text-sky-300 border-sky-700/40",
              lost:      "bg-red-900/40 text-red-400 border-red-700/40",
              expired:   "bg-gray-800 text-gray-500 border-gray-700",
            };
            const editing = editingLeadId === lead.lead_id;
            return (
              <div key={lead.lead_id || i}
                className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-3 space-y-2">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    {lead.lead?.buyer_name && (
                      <p className="text-white text-sm font-semibold mb-1">{lead.lead.buyer_name}</p>
                    )}
                    {editing ? (
                      <div className="space-y-1 max-w-sm">
                        <input value={editLeadDest} onChange={(e) => setEditLeadDest(e.target.value)}
                          className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-white text-xs" placeholder="destinations, comma separated" />
                        <div className="flex gap-1">
                          <input value={editLeadCat} onChange={(e) => setEditLeadCat(e.target.value)}
                            className="flex-1 bg-gray-900 border border-gray-700 rounded px-2 py-1 text-white text-xs" placeholder="category" />
                          <input value={editLeadBudget} onChange={(e) => setEditLeadBudget(e.target.value)}
                            className="flex-1 bg-gray-900 border border-gray-700 rounded px-2 py-1 text-white text-xs" placeholder="budget" />
                          <input value={editLeadPax} onChange={(e) => setEditLeadPax(e.target.value)} type="number"
                            className="w-16 bg-gray-900 border border-gray-700 rounded px-2 py-1 text-white text-xs" placeholder="pax" />
                        </div>
                        <div className="flex gap-1.5">
                          <button disabled={leadBusyId === lead.lead_id} onClick={() => saveLeadEdit(lead.lead_id)}
                            className="px-2 py-1 rounded bg-green-700 hover:bg-green-600 text-white text-[10px] font-semibold disabled:opacity-50">Save</button>
                          <button onClick={() => setEditingLeadId(null)}
                            className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white text-[10px] font-semibold">Cancel</button>
                        </div>
                      </div>
                    ) : (<>
                      <div className="flex flex-wrap gap-1">
                        {dests.map((d) => (
                          <span key={d} className="px-2 py-0.5 bg-amber-900/40 text-amber-300 text-[10px] rounded-full border border-amber-800/40">{d}</span>
                        ))}
                        {lead.lead?.category && (
                          <span className="px-2 py-0.5 bg-gray-700/60 text-gray-400 text-[10px] rounded-full">{lead.lead.category}</span>
                        )}
                      </div>
                      <div className="flex gap-3 mt-1.5 text-xs text-gray-500 flex-wrap">
                        {lead.lead?.budget && <span>Budget: <span className="text-emerald-300">{lead.lead.budget}</span></span>}
                        {lead.lead?.pax != null && <span>Pax: <span className="text-sky-300">{lead.lead.pax}</span></span>}
                        {lead.lead?.created_at && <span>{ago(lead.lead.created_at)}</span>}
                      </div>
                    </>)}
                  </div>
                  <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                    <span className={`px-2.5 py-1 rounded-lg text-xs font-bold border ${
                      score >= 70 ? "bg-amber-900/40 text-amber-300 border-amber-700/40"
                      : score >= 40 ? "bg-yellow-900/40 text-yellow-300 border-yellow-700/40"
                      : "bg-gray-800 text-gray-400 border-gray-700"
                    }`}>B: {(score * 100).toFixed(0)}</span>
                    {lead.lead?.status && (
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${STATUS_COLOR[lead.lead.status] ?? "bg-gray-800 text-gray-500 border-gray-700"}`}>
                        {lead.lead.status}
                      </span>
                    )}
                    {lead.lead_id && !editing && (
                      <div className="flex gap-1">
                        <button disabled={leadBusyId === lead.lead_id} onClick={() => startLeadEdit(lead)}
                          className="px-1.5 py-0.5 rounded bg-gray-700 hover:bg-gray-600 text-white text-[9px] font-semibold disabled:opacity-50">
                          Edit
                        </button>
                        <button disabled={leadBusyId === lead.lead_id} onClick={() => rerouteLead(lead.lead_id)}
                          className="px-1.5 py-0.5 rounded bg-gray-700 hover:bg-gray-600 text-white text-[9px] font-semibold disabled:opacity-50">
                          Reroute
                        </button>
                        <button disabled={leadBusyId === lead.lead_id} onClick={() => stopLead(lead.lead_id)}
                          className="px-1.5 py-0.5 rounded bg-red-800 hover:bg-red-700 text-white text-[9px] font-semibold disabled:opacity-50">
                          Stop
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {leadActionMsg && <p className="text-[11px] text-gray-500 px-1">{leadActionMsg}</p>}
        </div>

        {/* Deal Suggestions — Formula C */}
        {suggestions.length > 0 && (
          <div className="space-y-2 mt-2">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-1">
              Deal Opportunities — Formula C ({suggestions.length})
            </p>
            {suggestions.map((s, i) => (
              <div key={i} className="bg-gray-800/40 border border-cyan-900/30 rounded-xl p-3 flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-cyan-900/30 flex items-center justify-center flex-shrink-0 text-base">💡</div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-xs font-semibold">
                    {[s.corridor, s.vertical].filter(Boolean).join(" · ") || s.action}
                  </p>
                  <p className="text-gray-500 text-[11px] mt-0.5 leading-relaxed">{s.reason}</p>
                </div>
                <span className={`px-2 py-1 rounded-lg text-xs font-bold border flex-shrink-0 ${
                  s.demand_score >= 0.7 ? "bg-cyan-900/40 text-cyan-300 border-cyan-700/40"
                  : "bg-gray-800 text-gray-400 border-gray-700"
                }`}>{(s.demand_score * 100).toFixed(0)}</span>
              </div>
            ))}
          </div>
        )}

        {/* Seller as Buyer — Recommended Deals */}
        {buyerRecs.length > 0 && (
          <div className="space-y-2 mt-2">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-1">
              Buyer-Side Recommended Deals ({buyerRecs.length})
            </p>
            {buyerRecs.map((rec, i) => {
              const s = (Number(rec.score) || 0).toFixed(1);
              return (
                <div key={rec.deal_id || i}
                  className="bg-gray-800/40 border border-indigo-900/30 rounded-xl p-3 space-y-1.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-semibold truncate">{dealName(rec)}</p>
                      {rec.seller_name && (
                        <p className="text-amber-300 text-[11px] mt-0.5 truncate">{rec.seller_name}</p>
                      )}
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(rec.deal?.countries ?? []).map((c) => (
                          <span key={c} className="px-2 py-0.5 bg-indigo-900/40 text-indigo-300 text-[10px] rounded-full border border-indigo-800/40">{c}</span>
                        ))}
                        {rec.deal?.category && (
                          <span className="px-2 py-0.5 bg-gray-700/60 text-gray-400 text-[10px] rounded-full">{rec.deal.category}</span>
                        )}
                      </div>
                    </div>
                    <span className={`px-2.5 py-1 rounded-lg text-xs font-bold border flex-shrink-0 ${
                      Number(rec.score) >= 70 ? "bg-emerald-900/40 text-emerald-300 border-emerald-700/40"
                      : Number(rec.score) >= 40 ? "bg-amber-900/40 text-amber-300 border-amber-700/40"
                      : "bg-gray-800 text-gray-400 border-gray-700"
                    }`}>{s}</span>
                  </div>
                  {(rec.reason_strings ?? []).length > 0 && (
                    <p className="text-gray-500 text-[11px] leading-relaxed line-clamp-2">
                      {rec.reason_strings![0]}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* As a Buyer — Notifications received */}
        <div className="space-y-2 mt-2">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-1">
            Buyer-Side Notifications ({buyerNotifs.length})
          </p>
          {buyerNotifs.length === 0 && (
            <div className="text-center py-4 text-gray-600 text-sm">No buyer notifications yet</div>
          )}
          {buyerNotifs.map((n, i) => {
            const TRIGGER_COLOR: Record<string, string> = {
              new_listing:  "bg-indigo-900/40 text-indigo-300 border-indigo-800/40",
              idle_3d:      "bg-amber-900/40 text-amber-300 border-amber-800/40",
              price_drop:   "bg-emerald-900/40 text-emerald-300 border-emerald-800/40",
              early_access: "bg-violet-900/40 text-violet-300 border-violet-800/40",
            };
            const OUTCOME_COLOR: Record<string, string> = {
              clicked: "text-emerald-400", engaged: "text-sky-400",
              dismissed: "text-red-400", junk_marked: "text-red-500", ignored: "text-gray-600",
            };
            return (
              <div key={n.notification_id || i}
                className="bg-gray-800/40 border border-violet-900/30 rounded-xl px-3 py-2.5 space-y-1.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border flex-shrink-0 ${TRIGGER_COLOR[n.trigger_type ?? ""] ?? "bg-gray-800 text-gray-500 border-gray-700"}`}>
                    {n.trigger_type ?? "unknown"}
                  </span>
                  {n.tier_at_send && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-gray-800 text-gray-400 border border-gray-700 flex-shrink-0">{n.tier_at_send}</span>
                  )}
                  {n.formula_a_score != null && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-indigo-900/40 text-indigo-300 border border-indigo-800/40 flex-shrink-0">
                      A: {(Number(n.formula_a_score) || 0).toFixed(1)}
                    </span>
                  )}
                  <span className={`text-xs ml-auto flex-shrink-0 ${OUTCOME_COLOR[n.outcome ?? ""] ?? "text-gray-600"}`}>{n.outcome ?? "pending"}</span>
                  <span className="text-[10px] text-gray-700 flex-shrink-0">{n.sent_at ? ago(n.sent_at) : "—"}</span>
                </div>
                {n.title && <p className="text-white text-xs font-semibold">{n.title}</p>}
                {n.body  && <p className="text-gray-400 text-[11px] leading-relaxed">{n.body}</p>}
                <div className="flex flex-wrap gap-2 text-[10px]">
                  {(n.deal_title || n.deal_id) && (
                    <span className="text-gray-500">Deal: <span className="text-gray-300">{n.deal_title || `#${n.deal_id?.slice(-6)}`}</span></span>
                  )}
                  {n.seller_name && (
                    <span className="text-gray-500">Seller: <span className="text-amber-300">{n.seller_name}</span></span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Seller Notifications — full user-wise history with time-window filters + edit
            lives in the global Notifications tab. */}
        <div className="space-y-2 mt-2">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-1">
            Sent Notifications ({sellerNotifs.length})
          </p>
          {sellerNotifs.length === 0 && (
            <div className="text-center py-6 text-gray-600 text-sm">No notifications sent yet</div>
          )}
          {sellerNotifs.map((n, i) => {
            const TYPE_COLOR: Record<string, string> = {
              lead_alert:    "bg-amber-900/40 text-amber-300 border-amber-800/40",
              deal_suggestion: "bg-cyan-900/40 text-cyan-300 border-cyan-800/40",
              pricing_nudge: "bg-violet-900/40 text-violet-300 border-violet-800/40",
            };
            const TYPE_ICON: Record<string, string> = {
              lead_alert: "🔥", deal_suggestion: "💡", pricing_nudge: "💰",
            };
            return (
              <div key={n.log_id || i} className="bg-gray-800/40 border border-gray-700/40 rounded-xl px-3 py-2.5 space-y-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-base flex-shrink-0">{TYPE_ICON[n.type ?? ""] ?? "🔔"}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border flex-shrink-0 ${TYPE_COLOR[n.type ?? ""] ?? "bg-gray-800 text-gray-500 border-gray-700"}`}>
                    {n.type ?? "unknown"}
                  </span>
                  <span className="text-[10px] text-gray-700 ml-auto flex-shrink-0">{n.sent_at ? ago(n.sent_at) : "—"}</span>
                </div>
                {n.title && <p className="text-white text-xs font-semibold">{n.title}</p>}
                {n.body  && <p className="text-gray-400 text-[11px] leading-relaxed">{n.body}</p>}
                <div className="flex flex-wrap gap-2 text-[10px]">
                  {n.type === "lead_alert" && n.buyer_name && (
                    <span className="text-gray-500">Buyer: <span className="text-indigo-300">{n.buyer_name}</span></span>
                  )}
                  {n.type === "lead_alert" && !n.buyer_name && n.buyer_id && (
                    <span className="text-gray-600">Buyer ID: {n.buyer_id.slice(-8)}</span>
                  )}
                  {(n.deal_title || n.deal_id) && (
                    <span className="text-gray-500">Deal: <span className="text-gray-300">{n.deal_title || `#${n.deal_id?.slice(-6)}`}</span></span>
                  )}
                  {!n.deal_title && !n.deal_id && n.lead_id && (
                    <span className="text-gray-600">Lead #{n.lead_id.slice(-6)}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

      </>)}

      <div className="flex items-center justify-center gap-1.5 text-xs text-gray-700">
        <Pulse /> live · charts 1s · ws-push
        {lastAt && ` · ${lastAt.toLocaleTimeString()}`}
      </div>
    </div>
  );
};

// ─── Notifications Panel (global, cross-user) ─────────────────────────────────

interface AdminNotifItem {
  state_id?: string | null;
  log_id?: string;
  role: "buyer" | "seller";
  user_id?: string;
  user_name?: string;
  type?: string;
  title?: string;
  body?: string;
  deal_id?: string;
  lead_id?: string;
  sent_at?: string;
  next_due_at?: string;
  stage?: number;
  attempt_in_stage?: number;
  status: "sent" | "pending" | "stopped" | "suggested";
  opened?: boolean | null;
  candidate_id?: string; // set on "suggested" rows only — id to pass to trigger-candidate
}

interface AdminNotifCandidate {
  candidate_id: string;
  title: string;
  body: string;
  score?: number | null;
  already_sent: boolean;
}

// Global notification templates — editable common line (title prefix) per
// (trigger_type, variant), applies to every user immediately. Replaces the old
// per-user template editor. Each variant has slot_count lines, not one — the
// repeat-until-seen backoff followup re-pushes the same item several times,
// and each slot is the line used at that escalating retry (slot 0 = first
// send). slot_count is itself admin-configurable per trigger_type (shared
// across all its variants) — see NotificationTemplatesEditor's count control.
interface TemplateVariant {
  variant: string;
  default_lines: string[];
  line_overrides: (string | null)[];
}
interface NotifTemplateGroup {
  trigger_type: "new_listing" | "new_listing_followup" | "lead_alert" | "validity_missing";
  label: string;
  slot_count: number;
  variants: TemplateVariant[];
}

const VARIANT_LABEL: Record<string, string> = {
  free: "Free", pro: "Pro", studio: "Studio", elite: "Elite",
  new: "New", urgent: "Urgent", default: "Default",
};

const attemptSlotLabel = (i: number, count: number) =>
  i === 0 ? "Attempt 1 (first send)" : i === count - 1 ? `Attempt ${i + 1}+ (last call)` : `Attempt ${i + 1} (retry)`;

const GROUP_DESCRIPTION: Record<string, string> = {
  new_listing: "First push when a new deal matches — sent once per deal.",
  new_listing_followup: "Reminder if that push was never opened — retried on a backoff schedule, then stops.",
  lead_alert: "Sent to a seller when a lead is routed to them.",
  validity_missing: "Sent to a seller when their deal has no validity/expiry dates set — up to 2 times (default: 1h then 24h after upload), stops automatically once dates are added or the deal goes inactive.",
};

const NotificationTemplatesEditor = () => {
  const [groups, setGroups] = useState<NotifTemplateGroup[]>([]);
  const [loading, setLoading] = useState(false);
  // Variants start expanded — a collapsed-by-default accordion is exactly why
  // "click + to add an attempt" looked like it did nothing: the new slot was
  // real, just hidden inside a closed row nobody had a reason to open.
  const [collapsedVariants, setCollapsedVariants] = useState<Set<string>>(new Set());
  const [justAddedSlot, setJustAddedSlot] = useState<string | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { showError } = useAdminToast();
  useEffect(() => { if (error) showError(error); }, [error, showError]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await recApiClient.get<{ templates: NotifTemplateGroup[] }>(
        "/api/admin/rec-engine/notifications/templates", { headers: tok() }
      );
      setGroups(data.templates || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "failed to load templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const variantKey = (t: string, v: string) => `${t}:${v}`;
  const slotKey = (t: string, v: string, i: number) => `${t}:${v}:${i}`;

  const startEdit = (t: string, v: TemplateVariant, i: number) => {
    setEditingKey(slotKey(t, v.variant, i));
    setDraft(v.line_overrides[i] ?? v.default_lines[i]);
  };

  const save = async (t: string, v: TemplateVariant, i: number) => {
    const sk = slotKey(t, v.variant, i);
    setBusyKey(sk);
    try {
      await recApiClient.put(
        `/api/admin/rec-engine/notifications/templates/${t}/${v.variant}`,
        { attempt_index: i, line: draft }, { headers: tok() }
      );
      setEditingKey(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "save failed");
    } finally {
      setBusyKey(null);
    }
  };

  const resetToDefault = async (t: string, v: TemplateVariant, i: number) => {
    const sk = slotKey(t, v.variant, i);
    setBusyKey(sk);
    try {
      await recApiClient.put(
        `/api/admin/rec-engine/notifications/templates/${t}/${v.variant}`,
        { attempt_index: i, line: null }, { headers: tok() }
      );
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "reset failed");
    } finally {
      setBusyKey(null);
    }
  };

  const updateSlotCount = async (t: string, count: number, grew: boolean) => {
    setBusyKey(`${t}:slot-count`);
    try {
      await recApiClient.put(
        `/api/admin/rec-engine/notifications/templates/${t}/slot-count`,
        { count }, { headers: tok() }
      );
      await load();
      if (grew) {
        // Make sure every variant under this group is visibly open so the new
        // attempt row is impossible to miss, and flash it briefly.
        setCollapsedVariants((prev) => {
          const next = new Set(prev);
          for (const key of Array.from(next)) if (key.startsWith(`${t}:`)) next.delete(key);
          return next;
        });
        setJustAddedSlot(`${t}:${count - 1}`);
        setTimeout(() => setJustAddedSlot(null), 2500);
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "slot count update failed");
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex-shrink-0">
      <div className="flex items-center justify-between gap-1.5 mb-1">
        <div className="flex items-center gap-1.5">
          <h3 className="text-sm text-white font-semibold">Notification Wording</h3>
          <InfoTip text="The fixed opening line every notification of this type starts with, e.g. 'REC · New packages match your travel interests —'. Edit here and it applies to every buyer/seller immediately — the destination/deal-specific part after it always stays dynamic, nothing here is per-user." />
        </div>
        <RefreshButton onRefresh={load} className="px-2 py-1 text-xs" />
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Each pricing tier gets its own opening line, and each line changes as a notification retries — so a buyer who
        ignored the first push sees slightly different wording on the second nudge. Edit any line below, or add/remove
        retry attempts per notification type.
      </p>
      {error && <p className="text-red-400 text-xs mb-3 bg-red-950/30 border border-red-900/40 rounded-lg px-2.5 py-1.5">{error}</p>}
      {loading
        ? <div className="flex items-center justify-center h-16 gap-2 text-gray-500 text-xs"><Spin sz={4} /><Spinner /></div>
        : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {groups.map((g) => (
              <div key={g.trigger_type} className="bg-gray-800/40 border border-gray-700/50 rounded-lg p-3 space-y-2.5">
                <div>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-white text-sm font-semibold">{g.label}</p>
                    <div className="flex items-center gap-1.5 flex-shrink-0 bg-gray-900/60 border border-gray-700/50 rounded-lg px-1.5 py-1">
                      <button
                        title="Remove the last retry attempt"
                        disabled={busyKey === `${g.trigger_type}:slot-count` || g.slot_count <= 1}
                        onClick={() => updateSlotCount(g.trigger_type, g.slot_count - 1, false)}
                        className="w-6 h-6 flex items-center justify-center rounded-md bg-gray-700 hover:bg-gray-600 text-white text-sm font-bold disabled:opacity-30 disabled:cursor-not-allowed"
                      >−</button>
                      <span className="text-xs text-gray-200 font-semibold w-16 text-center">{g.slot_count} {g.slot_count === 1 ? "attempt" : "attempts"}</span>
                      <button
                        title="Add another retry attempt"
                        disabled={busyKey === `${g.trigger_type}:slot-count` || g.slot_count >= 10}
                        onClick={() => updateSlotCount(g.trigger_type, g.slot_count + 1, true)}
                        className="w-6 h-6 flex items-center justify-center rounded-md bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-bold disabled:opacity-30 disabled:cursor-not-allowed"
                      >+</button>
                    </div>
                  </div>
                  <p className="text-[11px] text-gray-500 mt-0.5">{GROUP_DESCRIPTION[g.trigger_type]}</p>
                </div>
                {g.variants.map((v) => {
                  const vk = variantKey(g.trigger_type, v.variant);
                  const collapsed = collapsedVariants.has(vk);
                  const hasAnyOverride = v.line_overrides.some((o) => !!o);
                  return (
                    <div key={vk} className="bg-gray-900/60 border border-gray-700/40 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setCollapsedVariants((prev) => {
                          const next = new Set(prev);
                          if (collapsed) next.delete(vk); else next.add(vk);
                          return next;
                        })}
                        className="w-full flex items-center gap-2 text-left px-2.5 py-2 hover:bg-gray-800/40 transition-colors"
                      >
                        <span className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold flex-shrink-0 bg-gray-800 rounded px-1.5 py-0.5">
                          {VARIANT_LABEL[v.variant] || v.variant}
                        </span>
                        {hasAnyOverride && (
                          <span className="text-[10px] text-indigo-300 bg-indigo-500/15 border border-indigo-500/30 rounded-full px-1.5 py-0.5 flex-shrink-0">Customized</span>
                        )}
                        <span className="flex-1 text-right text-gray-500 text-xs flex-shrink-0">
                          {collapsed ? `Show ${g.slot_count} ${g.slot_count === 1 ? "line" : "lines"} ▾` : "Hide ▴"}
                        </span>
                      </button>
                      {!collapsed && (
                        <div className="px-2.5 pb-2.5 space-y-2 border-t border-gray-800">
                          {Array.from({ length: g.slot_count }, (_, i) => {
                            const sk = slotKey(g.trigger_type, v.variant, i);
                            const editing = editingKey === sk;
                            const busy = busyKey === sk;
                            const hasOverride = !!v.line_overrides[i];
                            const isNew = justAddedSlot === `${g.trigger_type}:${i}`;
                            return (
                              <div
                                key={sk}
                                className={`pt-2 rounded-md transition-colors ${isNew ? "bg-indigo-500/10 ring-1 ring-indigo-500/40 px-1.5" : ""}`}
                              >
                                <div className="flex items-center gap-1.5 mb-1">
                                  <p className="text-[11px] text-gray-400 font-medium">{attemptSlotLabel(i, g.slot_count)}</p>
                                  {isNew && <span className="text-[10px] text-indigo-300 font-semibold">← just added, edit its line below</span>}
                                </div>
                                {editing
                                  ? <div className="space-y-1.5">
                                      <input value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus
                                        className="w-full bg-gray-800 border border-indigo-600/60 rounded-md px-2 py-1.5 text-white text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                                      <div className="flex gap-1.5">
                                        <button disabled={busy} onClick={() => save(g.trigger_type, v, i)}
                                          className="px-2.5 py-1 rounded-md bg-emerald-700 hover:bg-emerald-600 text-white text-xs font-semibold disabled:opacity-50">Save</button>
                                        <button onClick={() => setEditingKey(null)}
                                          className="px-2.5 py-1 rounded-md bg-gray-700 hover:bg-gray-600 text-white text-xs font-semibold">Cancel</button>
                                      </div>
                                    </div>
                                  : <div className="flex items-start gap-2">
                                      <p className="flex-1 min-w-0 text-gray-200 text-sm leading-snug" title={v.line_overrides[i] || v.default_lines[i]}>
                                        {v.line_overrides[i] || v.default_lines[i]}
                                      </p>
                                      <div className="flex items-center gap-1 flex-shrink-0">
                                        <button disabled={busy} onClick={() => startEdit(g.trigger_type, v, i)}
                                          className="px-2 py-1 rounded-md bg-gray-700 hover:bg-gray-600 text-white text-[11px] font-semibold disabled:opacity-50">Edit</button>
                                        {hasOverride && (
                                          <button disabled={busy} title="Reset to default wording" onClick={() => resetToDefault(g.trigger_type, v, i)}
                                            className="px-2 py-1 rounded-md bg-gray-800 hover:bg-gray-700 text-gray-400 text-[11px] font-semibold disabled:opacity-50">Reset</button>
                                        )}
                                      </div>
                                    </div>
                                }
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )
      }
    </div>
  );
};

// Global kill switch — pause EVERY future notification (all users, all trigger types,
// scheduled and admin-manual) for a duration or indefinitely. Distinct from the per-user
// "Stop All Notifications" button below, which only mutes one selected user.
const PAUSE_PRESETS = [
  { label: "1 hour", hours: 1 },
  { label: "24 hours", hours: 24 },
  { label: "3 days", hours: 72 },
  { label: "7 days", hours: 168 },
] as const;

const fmtUntil = (iso: string | null) => {
  if (!iso) return "";
  const d = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : iso + "Z");
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

type PauseScope = "all" | "buyer" | "seller";

const SCOPE_LABEL: Record<PauseScope, string> = {
  all: "Everything",
  buyer: "Deal notifications (buyers + sellers browsing as buyers)",
  seller: "Lead alerts (sellers only)",
};

const GlobalPauseControl = () => {
  const [paused, setPaused] = useState(false);
  const [pausedUntil, setPausedUntil] = useState<string | null>(null);
  const [pausedScope, setPausedScope] = useState<PauseScope>("all");
  const [scope, setScope] = useState<PauseScope>("all"); // scope picked for the NEXT pause action
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showError } = useAdminToast();
  useEffect(() => { if (error) showError(error); }, [error, showError]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await recApiClient.get<{ paused: boolean; paused_until: string | null; scope: PauseScope }>(
        "/api/admin/rec-engine/notifications/pause-status", { headers: tok() }
      );
      setPaused(data.paused);
      setPausedUntil(data.paused_until);
      setPausedScope(data.scope);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "failed to load pause status");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pauseFor = async (hours: number | null) => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await recApiClient.post<{ paused: boolean; paused_until: string | null; scope: PauseScope }>(
        "/api/admin/rec-engine/notifications/pause", { duration_hours: hours, scope }, { headers: tok() }
      );
      setPaused(data.paused);
      setPausedUntil(data.paused_until);
      setPausedScope(data.scope);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "pause failed");
    } finally {
      setBusy(false);
    }
  };

  const resume = async () => {
    setBusy(true);
    setError(null);
    try {
      await recApiClient.post("/api/admin/rec-engine/notifications/resume", {}, { headers: tok() });
      setPaused(false);
      setPausedUntil(null);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "resume failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`rounded-xl p-3 flex-shrink-0 border ${paused ? "bg-red-950/30 border-red-800/50" : "bg-gray-900 border-gray-800"}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] uppercase tracking-wider font-semibold flex items-center text-gray-500">
          Global notification kill switch
          <InfoTip text="Stops future notification sends for everyone at once. 'Deal notifications' = New Listing + Matching Deal, sent to BOTH buyer accounts and seller accounts browsing deals as a buyer — pausing it stops deal pushes for everyone, regardless of account type. 'Lead alerts' = only ever sent to seller accounts receiving routed leads; buyers never get these. Blocks scheduled sends AND admin-manual triggers. Separate from the per-user 'Stop All Notifications' button on the Notifications tab, which only mutes one selected user." />
        </p>
        <div className="flex items-center gap-2">
          {!loading && (
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
              paused ? "bg-red-900/40 text-red-300 border-red-800/40" : "bg-emerald-900/40 text-emerald-300 border-emerald-800/40"
            }`}>
              {paused
                ? `⏸ Paused (${SCOPE_LABEL[pausedScope]}) ${pausedUntil ? `until ${fmtUntil(pausedUntil)}` : "— indefinitely"}`
                : "✓ Active — all notifications sending normally"}
            </span>
          )}
          <RefreshButton onRefresh={load} className="px-2 py-1 text-xs" />
        </div>
      </div>
      {error && <p className="text-red-400 text-xs mb-1.5">{error}</p>}
      {loading
        ? <div className="flex items-center gap-2 text-gray-500 text-xs"><Spin sz={4} /><Spinner /></div>
        : <div className="space-y-2">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] text-gray-500 flex-shrink-0">Pause scope:</span>
              <div className="flex gap-1 bg-gray-800/60 p-0.5 rounded-lg">
                {(["all", "buyer", "seller"] as const).map((s) => (
                  <button key={s} onClick={() => setScope(s)} title={SCOPE_LABEL[s]}
                    className={`px-2 py-0.5 text-[11px] font-semibold rounded-md transition-all ${
                      scope === s ? "bg-indigo-500/20 text-indigo-300" : "text-gray-500 hover:text-gray-300"
                    }`}>
                    {s === "all" ? "Everything" : s === "buyer" ? "Deal notifications" : "Lead alerts"}
                  </button>
                ))}
              </div>
              <span className="text-[10px] text-gray-600">
                {scope === "buyer" ? "(buyers + sellers-as-buyers)" : scope === "seller" ? "(sellers only — buyers never get leads)" : ""}
              </span>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {PAUSE_PRESETS.map((p) => (
                <button key={p.hours} disabled={busy} onClick={() => pauseFor(p.hours)}
                  className="px-2.5 py-1 rounded bg-gray-800 hover:bg-gray-700 text-gray-300 text-[11px] font-semibold disabled:opacity-50">
                  Pause {p.label}
                </button>
              ))}
              <button disabled={busy} onClick={() => pauseFor(null)}
                className="px-2.5 py-1 rounded bg-red-900/60 hover:bg-red-900 text-red-200 text-[11px] font-semibold disabled:opacity-50">
                Pause indefinitely
              </button>
              {paused && (
                <button disabled={busy} onClick={resume}
                  className="px-2.5 py-1 rounded bg-emerald-700 hover:bg-emerald-600 text-white text-[11px] font-semibold disabled:opacity-50">
                  ▶ Resume now
                </button>
              )}
            </div>
          </div>
      }
    </div>
  );
};

interface BackoffStageRow { interval_hours: number; max_attempts: number; }

const BACKOFF_KEYS = [
  { key: "buyer_deal_followup", label: "Buyer — matched deal follow-up" },
  { key: "seller_lead_followup", label: "Seller — lead alert follow-up" },
  { key: "seller_validity_followup", label: "Seller — missing validity follow-up" },
  { key: "seller_pricing_nudge_followup", label: "Seller — pricing nudge follow-up" },
] as const;

// Clear names instead of bare "stage 1" / "stage 2" — default cadence is a short-gap
// quick retry wave, then a long-gap extended wave before giving up.
const BACKOFF_STAGE_LABEL = ["Quick retry", "Extended retry", "Final retry"];

// Static tab list per role — buyer never gets Lead or Pricing Nudge (both seller-only).
// "pricing_nudge" isn't part of NotifTemplateGroup (that's the tiered-opening-line system
// for new_listing/lead_alert/etc) — it's a separate title+body-per-nudge-type system, see
// the Pricing Nudge Wording card below. Included here purely so sent pricing_nudge
// notifications (notification_log.type === "pricing_nudge") are browsable per-user.
const TABS_FOR_ROLE: Record<"buyer" | "seller", { trigger_type: NotifTemplateGroup["trigger_type"] | "pricing_nudge"; label: string }[]> = {
  buyer: [
    { trigger_type: "new_listing", label: "New Listing" },
    { trigger_type: "new_listing_followup", label: "Matching Deal" },
  ],
  seller: [
    { trigger_type: "new_listing", label: "New Listing" },
    { trigger_type: "new_listing_followup", label: "Matching Deal" },
    { trigger_type: "lead_alert", label: "Lead" },
    { trigger_type: "pricing_nudge", label: "Pricing Nudge" },
    { trigger_type: "validity_missing", label: "Missing Validity" },
  ],
};

const BackoffCadenceEditor = () => {
  const [backoff, setBackoff] = useState<Record<string, BackoffStageRow[]>>({});
  const [backoffEnabled, setBackoffEnabled] = useState<Record<string, boolean>>({});
  const [backoffSaving, setBackoffSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { showError } = useAdminToast();
  useEffect(() => { if (error) showError(error); }, [error, showError]);

  const loadBackoff = useCallback(async () => {
    for (const { key } of BACKOFF_KEYS) {
      try {
        const { data } = await recApiClient.get<any>(`/api/admin/rec-engine/backoff-config/${key}`, { headers: tok() });
        setBackoff((prev) => ({ ...prev, [key]: data.stages || [] }));
        setBackoffEnabled((prev) => ({ ...prev, [key]: data.enabled ?? true }));
      } catch { /* leave unset — form shows defaults */ }
    }
  }, []);

  useEffect(() => { loadBackoff(); }, [loadBackoff]);

  const saveBackoff = async (key: string) => {
    setBackoffSaving(key);
    setError(null);
    try {
      await recApiClient.put(`/api/admin/rec-engine/backoff-config/${key}`,
        { enabled: backoffEnabled[key] ?? true, stages: backoff[key] || [] }, { headers: tok() });
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "backoff save failed");
    } finally {
      setBackoffSaving(null);
    }
  };

  const updateStage = (key: string, idx: number, field: keyof BackoffStageRow, value: number) => {
    setBackoff((prev) => {
      const stages = [...(prev[key] || [])];
      stages[idx] = { ...stages[idx], [field]: value };
      return { ...prev, [key]: stages };
    });
  };

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex-shrink-0">
      <div className="flex items-center justify-between mb-3">
        <p className="text-white font-semibold text-sm flex items-center">
          Repeat-until-seen backoff cadence
          <InfoTip text="How many times and how far apart we re-push a matched deal, lead alert, or missing-validity nudge if the user hasn't acted on it yet. Applies to every buyer/seller — stops automatically for a given user once they open it, fix it, or the last stage runs out." />
        </p>
        <RefreshButton onRefresh={loadBackoff} className="px-2 py-1 text-xs" />
      </div>
      {error && <p className="text-red-400 text-xs mb-2">{error}</p>}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {BACKOFF_KEYS.map(({ key, label }) => (
          <div key={key} className="bg-gray-800/40 border border-gray-700/40 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-gray-300 text-xs font-semibold">{label}</p>
              <label className="flex items-center gap-1.5 text-[11px] text-gray-500">
                <input type="checkbox" checked={backoffEnabled[key] ?? true}
                  onChange={(e) => setBackoffEnabled((p) => ({ ...p, [key]: e.target.checked }))} />
                enabled
              </label>
            </div>
            {(backoff[key] || []).map((stage, idx) => (
              <div key={idx} className="flex items-center gap-2 text-xs">
                <span className="text-gray-500 w-28 flex-shrink-0">{BACKOFF_STAGE_LABEL[idx] ?? `Wave ${idx + 1}`}</span>
                <input type="number" min={1} value={stage.interval_hours}
                  onChange={(e) => updateStage(key, idx, "interval_hours", Number(e.target.value))}
                  className="w-16 bg-gray-900 border border-gray-700 rounded px-2 py-1 text-white" />
                <span className="text-gray-600">hrs ×</span>
                <input type="number" min={1} value={stage.max_attempts}
                  onChange={(e) => updateStage(key, idx, "max_attempts", Number(e.target.value))}
                  className="w-14 bg-gray-900 border border-gray-700 rounded px-2 py-1 text-white" />
                <span className="text-gray-600">attempts</span>
              </div>
            ))}
            <button onClick={() => saveBackoff(key)} disabled={backoffSaving === key}
              className="mt-1 px-3 py-1 rounded-lg text-[11px] font-semibold bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50">
              {backoffSaving === key ? "Saving…" : "Save cadence"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Pricing Nudge Wording — title/body for the 6 seller pricing-intelligence nudges ──
// ─── (see PricingIntelligenceEngine.analyze). Separate from Notification Wording ──────
// ─── above: those are tiered opening-line slots (ELITE/STUDIO/PRO/FREE), this is a ───
// ─── flat title+body per nudge_type, backed by the same notification_templates ────────
// ─── collection fire_pricing_nudge() reads at send time. ───────────────────────────────
interface PricingNudgeRow {
  nudge_type: string;
  title: string;
  body: string;
  is_customized: boolean;
}

const NUDGE_TYPE_LABEL: Record<string, string> = {
  underpriced: "Underpriced",
  competitor_undercut_alert: "Competitor Undercut",
  seasonal_markup_opportunity: "Seasonal Markup",
  premium_pricing_opportunity: "Premium Pricing",
  conversion_optimal: "Conversion Optimal",
  stale_price_warning: "Stale Price",
};

const PricingNudgeWordingEditor = () => {
  const [rows, setRows] = useState<PricingNudgeRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingType, setEditingType] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftBody, setDraftBody] = useState("");
  const [busyType, setBusyType] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { showError } = useAdminToast();
  useEffect(() => { if (error) showError(error); }, [error, showError]);

  // Dedicated on/off switch for pricing nudges only — separate from the shared
  // "seller" scope kill switch above. Defaults to disabled on the backend until
  // an admin explicitly turns it on.
  const [enabled, setEnabled] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [toggleBusy, setToggleBusy] = useState(false);

  const loadToggle = useCallback(async () => {
    setToggleLoading(true);
    try {
      const { data } = await recApiClient.get<{ enabled: boolean }>(
        "/api/admin/rec-engine/pricing-nudges/settings", { headers: tok() }
      );
      setEnabled(data.enabled);
    } catch {
      setEnabled(false);
    } finally {
      setToggleLoading(false);
    }
  }, []);

  const toggleEnabled = async () => {
    setToggleBusy(true);
    setError(null);
    const next = !enabled;
    try {
      await recApiClient.post(
        "/api/admin/rec-engine/pricing-nudges/settings", { enabled: next }, { headers: tok() }
      );
      setEnabled(next);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "toggle failed");
    } finally {
      setToggleBusy(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await recApiClient.get<{ items: PricingNudgeRow[] }>(
        "/api/admin/rec-engine/pricing-nudges/templates", { headers: tok() }
      );
      setRows(data.items || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "failed to load pricing nudge templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); loadToggle(); }, [load, loadToggle]);

  const startEdit = (row: PricingNudgeRow) => {
    setEditingType(row.nudge_type);
    setDraftTitle(row.title);
    setDraftBody(row.body);
    setError(null);
  };

  const cancelEdit = () => {
    setEditingType(null);
    setDraftTitle("");
    setDraftBody("");
  };

  const save = async (nudgeType: string) => {
    setBusyType(nudgeType);
    setError(null);
    try {
      await recApiClient.put(
        `/api/admin/rec-engine/pricing-nudges/templates/${nudgeType}`,
        { title: draftTitle, body: draftBody }, { headers: tok() }
      );
      setEditingType(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "save failed");
    } finally {
      setBusyType(null);
    }
  };

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex-shrink-0">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-1.5">
          <h3 className="text-sm text-white font-semibold">💰 Pricing Nudge Wording</h3>
          <InfoTip text="Title + body for the 6 pricing-intelligence nudges (see PricingIntelligenceEngine.analyze). Edit here and it applies to every seller immediately. Uncustomized rows show the built-in default copy. Detected daily, sent twice per (deal, nudge type): immediately, then 24h later — cadence editable in Seller — pricing nudge follow-up below." />
        </div>
        {!toggleLoading && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
              enabled ? "bg-emerald-900/40 text-emerald-300 border-emerald-800/40" : "bg-red-900/40 text-red-300 border-red-800/40"
            }`}>
              {enabled ? "✓ Active" : "⏸ Off"}
            </span>
            <button onClick={toggleEnabled} disabled={toggleBusy}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold disabled:opacity-50 ${
                enabled ? "bg-red-900/60 hover:bg-red-900 text-red-200" : "bg-emerald-700 hover:bg-emerald-600 text-white"
              }`}>
              {toggleBusy ? "Saving…" : enabled ? "Turn off" : "Turn on"}
            </button>
            <RefreshButton onRefresh={async () => { await Promise.all([load(), loadToggle()]); }} className="px-2 py-1 text-xs" />
          </div>
        )}
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Sent when a seller's deal triggers a pricing insight — underpriced, competitor undercut, seasonal markup,
        premium pricing opportunity, conversion-optimal, or stale price.
      </p>
      {error && <p className="text-red-400 text-xs mb-3 bg-red-950/30 border border-red-900/40 rounded-lg px-2.5 py-1.5">{error}</p>}
      {loading
        ? <div className="flex items-center justify-center h-16 gap-2 text-gray-500 text-xs"><Spin sz={4} /><Spinner /></div>
        : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {rows.map((row) => (
              <div key={row.nudge_type} className="bg-gray-800/40 border border-gray-700/50 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-white text-sm font-semibold">{NUDGE_TYPE_LABEL[row.nudge_type] || row.nudge_type}</p>
                  {!row.is_customized && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-700/60 text-gray-400 border border-gray-600/40">Default</span>
                  )}
                </div>
                {editingType === row.nudge_type ? (
                  <div className="space-y-1.5">
                    <input value={draftTitle} onChange={(e) => setDraftTitle(e.target.value)}
                      placeholder="Title"
                      className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-xs text-white focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                    <textarea value={draftBody} onChange={(e) => setDraftBody(e.target.value)}
                      placeholder="Body" rows={2}
                      className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-xs text-white focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => save(row.nudge_type)} disabled={busyType === row.nudge_type}
                        className="px-2.5 py-1 rounded text-[11px] font-semibold bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50">
                        {busyType === row.nudge_type ? "Saving…" : "Save"}
                      </button>
                      <button onClick={cancelEdit} className="px-2.5 py-1 rounded text-[11px] font-semibold text-gray-400 hover:text-white">
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <p className="text-gray-300 text-xs">{row.title}</p>
                    <p className="text-gray-500 text-[11px]">{row.body}</p>
                    <button onClick={() => startEdit(row)}
                      className="text-[11px] font-semibold text-indigo-400 hover:text-indigo-300">
                      Edit
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      }
    </div>
  );
};

// ─── Notification Settings (global, cross-user — everything an admin edits once and it ──
// ─── applies everywhere: title lines, kill switch, backoff cadence) ─────────────────────
const NotificationSettingsPanel = () => (
  <div className="flex-1 flex flex-col gap-4 min-h-0 overflow-y-auto p-1">
    <GlobalPauseControl />
    <ProfilePushSettingsEditor />
    <NotificationTemplatesEditor />
    <PricingNudgeWordingEditor />
    <BackoffCadenceEditor />
  </div>
);

const NotificationsPanel = () => {
  // ── User picker — "user wise" browsing, buyer or seller ──────────────────
  const [pickerRole,    setPickerRole]    = useState<"buyer" | "seller">("buyer");
  const [pickerSearch,  setPickerSearch]  = useState("");
  const [pickerUsers,   setPickerUsers]   = useState<AdminUser[]>([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [selectedUser,  setSelectedUser]  = useState<AdminUser | null>(null);

  // ── Selected user's notifications ─────────────────────────────────────────
  const [items,   setItems]   = useState<AdminNotifItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [notifWindow, setNotifWindow] = useState<TimeWindow>("all");
  // One tab per trigger type — buyers get 2 (New Listing, Matching Deal), sellers get all 3
  // (+ Lead). Each tab shows that type's full sent history; copy itself is edited once,
  // globally, in the Notification Templates panel above (not per user).
  const [activeType, setActiveType] = useState<NotifTemplateGroup["trigger_type"] | "pricing_nudge">("new_listing");
  const [busyKey,      setBusyKey]      = useState<string | null>(null);
  const [error,        setError]        = useState<string | null>(null);
  const { showError } = useAdminToast();
  useEffect(() => { if (error) showError(error); }, [error, showError]);

  // Full admin control per selected user — force-send now, or mute/unmute everything
  const [muted, setMuted] = useState(false);
  const [muteLoading, setMuteLoading] = useState(false);
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [controlMsg, setControlMsg] = useState<string | null>(null);

  // Candidates — every deal/lead the rec engine currently matches, not just already-sent
  // ones. Only meaningful for "new_listing" (buyer) and "lead_alert" (seller) — a
  // matched-deal follow-up or a not-yet-first-sent lead has no separate "candidate" concept.
  const [candidates, setCandidates] = useState<AdminNotifCandidate[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidateBusyId, setCandidateBusyId] = useState<string | null>(null);

  const [pickerPage, setPickerPage] = useState(1);
  const [pickerHasMore, setPickerHasMore] = useState(false);

  const searchPickerUsers = useCallback(async (role: "buyer" | "seller", q: string, pg: number) => {
    setPickerLoading(true);
    try {
      const params: Record<string, string | number> = { role, page: pg, page_size: 15 };
      if (q) params.search = q;
      const { data } = await apiClient.get<any>("/api/admin/users", { params, headers: tok() });
      const list = data.items || data.users || data.data || [];
      setPickerUsers(pg === 1 ? list : (prev) => [...prev, ...list]);
      setPickerHasMore((data.page ?? pg) < (data.total_pages ?? data.pages ?? 1));
    } catch {
      if (pg === 1) setPickerUsers([]);
    } finally {
      setPickerLoading(false);
    }
  }, []);

  useEffect(() => {
    setPickerPage(1);
    const t = setTimeout(() => searchPickerUsers(pickerRole, pickerSearch, 1), 300);
    return () => clearTimeout(t);
  }, [pickerRole, pickerSearch, searchPickerUsers]);

  const loadMorePickerUsers = () => {
    const np = pickerPage + 1;
    setPickerPage(np);
    searchPickerUsers(pickerRole, pickerSearch, np);
  };

  // Note: user's role here is *how we found them* (buyer list vs seller list) — a seller
  // still shows up in both their own lead/deal-suggestion notifications AND any matched-deal
  // notifications they got as a buyer (sellers browse deals too). A pure buyer never gets
  // lead-type notifications, since leads are never routed to non-sellers.
  const load = useCallback(async () => {
    if (!selectedUser) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await recApiClient.get<{ notifications: AdminNotifItem[] }>(
        "/api/admin/rec-engine/notifications", { params: { user_id: selectedUser.id, limit: 200 }, headers: tok() }
      );
      setItems(data.notifications || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "failed to load notifications");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [selectedUser]);

  const loadMuteStatus = useCallback(async () => {
    if (!selectedUser) return;
    try {
      const { data } = await recApiClient.get<{ muted: boolean }>(
        `/api/admin/rec-engine/notifications/${pickerRole}/${selectedUser.id}/mute-status`, { headers: tok() }
      );
      setMuted(!!data.muted);
    } catch {
      setMuted(false);
    }
  }, [selectedUser, pickerRole]);

  // "new_listing" has a real candidate pool (Formula A matches); "lead_alert" has one too
  // (Formula B lead inbox). "new_listing_followup" doesn't — it only exists after a first
  // send, which the sent-history list already covers in full.
  const CANDIDATE_ELIGIBLE = activeType === "new_listing" || activeType === "lead_alert";

  const loadCandidates = useCallback(async () => {
    if (!selectedUser || !CANDIDATE_ELIGIBLE || notifWindow !== "all") {
      setCandidates([]);
      return;
    }
    setCandidatesLoading(true);
    try {
      const { data } = await recApiClient.get<{ candidates: AdminNotifCandidate[] }>(
        `/api/admin/rec-engine/notifications/${pickerRole}/${selectedUser.id}/candidates`, { headers: tok() }
      );
      setCandidates((data.candidates || []).filter((c) => !c.already_sent));
    } catch {
      setCandidates([]);
    } finally {
      setCandidatesLoading(false);
    }
  }, [selectedUser, pickerRole, CANDIDATE_ELIGIBLE, notifWindow]);

  const triggerCandidate = async (c: AdminNotifCandidate) => {
    if (!selectedUser) return;
    setCandidateBusyId(c.candidate_id);
    try {
      await recApiClient.post(
        `/api/admin/rec-engine/notifications/${pickerRole}/${selectedUser.id}/trigger-candidate`,
        { candidate_id: c.candidate_id }, { headers: tok() }
      );
      await Promise.all([load(), loadCandidates()]);
    } catch (e: any) {
      setControlMsg(e?.response?.data?.detail || e?.message || "trigger failed");
    } finally {
      setCandidateBusyId(null);
    }
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setActiveType("new_listing"); setControlMsg(null); }, [selectedUser?.id, pickerRole]);
  useEffect(() => { loadMuteStatus(); }, [loadMuteStatus]);
  useEffect(() => { loadCandidates(); }, [loadCandidates]);

  const triggerNowForUser = async () => {
    if (!selectedUser) return;
    setTriggerLoading(true);
    setControlMsg(null);
    try {
      const { data } = await recApiClient.post<{ sent: boolean }>(
        `/api/admin/rec-engine/notifications/${pickerRole}/${selectedUser.id}/trigger-now`, {}, { headers: tok() }
      );
      setControlMsg(data.sent ? "Notification sent." : "Nothing to send — gates blocked delivery (check FCM/device token).");
      await load();
    } catch (e: any) {
      setControlMsg(e?.response?.data?.detail || e?.message || "trigger failed");
    } finally {
      setTriggerLoading(false);
    }
  };

  const toggleMuteForUser = async () => {
    if (!selectedUser) return;
    setMuteLoading(true);
    setControlMsg(null);
    try {
      const action = muted ? "unmute" : "mute";
      await recApiClient.post(`/api/admin/rec-engine/notifications/${pickerRole}/${selectedUser.id}/${action}`, {}, { headers: tok() });
      setMuted(!muted);
      setControlMsg(muted ? "Notifications resumed for this user." : "All future notifications stopped for this user.");
      await load();
    } catch (e: any) {
      setControlMsg(e?.response?.data?.detail || e?.message || "mute toggle failed");
    } finally {
      setMuteLoading(false);
    }
  };

  const rowKey = (n: AdminNotifItem) => n.state_id || n.log_id || n.candidate_id || `${n.role}:${n.user_id}:${n.type}:${n.sent_at || n.next_due_at}`;

  // One trigger type per tab. "Lead" tab covers both the initial lead_alert push and its
  // repeat-until-seen follow-up — same underlying notification, just re-sent.
  const typeFiltered = items.filter((n) =>
    activeType === "lead_alert" ? (n.type === "lead_alert" || n.type === "lead_alert_followup") : n.type === activeType
  );
  // Time window applies to historical (sent) rows only — pending/stopped follow-ups are
  // forward-looking action items, not noise to be scrolled away by an age filter.
  const historyVisible = typeFiltered.filter((n) => n.status !== "sent" || withinWindow(n.sent_at, notifWindow));
  // "All time" also surfaces every currently-matching deal/lead that hasn't been sent yet —
  // not just history — so the admin sees the full picture the rec engine is working with.
  const suggestedRows: AdminNotifItem[] = candidates.map((c) => ({
    role: pickerRole,
    type: activeType,
    title: c.title,
    body: c.body,
    status: "suggested",
    candidate_id: c.candidate_id,
  }));
  const visibleItems = [...historyVisible, ...suggestedRows];

  const triggerNow = async (n: AdminNotifItem) => {
    if (!n.state_id) return;
    setBusyKey(rowKey(n));
    try {
      await recApiClient.post(`/api/admin/rec-engine/notifications/${n.role}/${n.state_id}/trigger`, {}, { headers: tok() });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "trigger failed");
    } finally {
      setBusyKey(null);
    }
  };

  const stopNotif = async (n: AdminNotifItem) => {
    if (!n.state_id) return;
    setBusyKey(rowKey(n));
    try {
      await recApiClient.post(`/api/admin/rec-engine/notifications/${n.role}/${n.state_id}/stop`, {}, { headers: tok() });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "stop failed");
    } finally {
      setBusyKey(null);
    }
  };

  const STATUS_COLOR: Record<string, string> = {
    sent:      "bg-green-900/40 text-green-300 border-green-800/40",
    pending:   "bg-amber-900/40 text-amber-300 border-amber-800/40",
    stopped:   "bg-gray-800 text-gray-500 border-gray-700",
    suggested: "bg-sky-900/40 text-sky-300 border-sky-800/40",
  };

  return (
    <div className="flex-1 flex gap-4 min-h-0">

      {/* User picker */}
      <div className="w-64 flex flex-col bg-gray-900 border border-gray-800 rounded-xl overflow-hidden flex-shrink-0">
        <div className="p-3 border-b border-gray-800 flex-shrink-0 space-y-2">
          <p className="text-[10px] text-gray-500 flex items-center">
            Browse notifications user-wise
            <InfoTip text="Pick a buyer or seller below to see everything the rec engine has sent them, filterable by time window and (for sellers) split into deal vs lead notifications." />
          </p>
          <div className="flex gap-1 bg-gray-800/60 p-0.5 rounded-lg">
            {(["buyer", "seller"] as const).map((r) => (
              <button key={r} onClick={() => { setPickerRole(r); setSelectedUser(null); }}
                className={`flex-1 py-1 text-xs font-semibold rounded-md transition-all ${
                  pickerRole === r ? (r === "buyer" ? "bg-indigo-500/20 text-indigo-300" : "bg-amber-500/20 text-amber-300") : "text-gray-500"
                }`}>
                {r === "buyer" ? "👤 Buyers" : "🏢 Sellers"}
              </button>
            ))}
          </div>
          <input value={pickerSearch} onChange={(e) => setPickerSearch(e.target.value)}
            placeholder={`Search ${pickerRole}s…`}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500" />
        </div>
        <div className="flex-1 overflow-y-auto">
          {pickerLoading && pickerUsers.length === 0
            ? <div className="flex items-center justify-center h-20 gap-2 text-gray-500 text-xs"><Spin sz={4} /><Spinner /></div>
            : pickerUsers.length === 0
              ? <div className="flex items-center justify-center h-20 text-gray-600 text-xs">No {pickerRole}s found</div>
              : (<>
                  {pickerUsers.map((u) => (
                    <button key={u.id} onClick={() => setSelectedUser(u)}
                      className={`w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-gray-800/60 transition-colors border-b border-gray-800/40 ${
                        selectedUser?.id === u.id ? "bg-gray-800 border-l-2 border-l-indigo-500" : ""
                      }`}>
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center font-semibold text-[10px] flex-shrink-0 ${
                        pickerRole === "buyer" ? "bg-indigo-500/20 text-indigo-400" : "bg-amber-500/20 text-amber-400"
                      }`}>{u.name[0]?.toUpperCase()}</div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-white truncate">{u.name}</p>
                        <p className="text-[10px] text-gray-500 truncate">{u.email}</p>
                      </div>
                    </button>
                  ))}
                  {pickerHasMore && (
                    <button onClick={loadMorePickerUsers} disabled={pickerLoading}
                      className="w-full py-2.5 text-[11px] text-indigo-400 hover:text-indigo-300 hover:bg-gray-800/40 transition-colors disabled:opacity-50">
                      {pickerLoading ? "Loading…" : "Load more"}
                    </button>
                  )}
                </>)
          }
        </div>
      </div>

      {/* Selected user's notifications */}
      <div className="flex-1 flex flex-col gap-4 min-h-0 overflow-y-auto p-1">

        {!selectedUser ? (
          <div className="flex-1 flex items-center justify-center text-gray-600 text-sm">
            Select a {pickerRole} on the left to see their notifications
          </div>
        ) : (<>
          <div className="flex items-center justify-between flex-shrink-0">
            <p className="text-white font-semibold text-sm">
              {selectedUser.name} <span className="text-gray-500 font-normal text-xs">({selectedUser.email})</span>
            </p>
            <div className="flex items-center gap-2">
              <span className="flex items-center text-[10px] text-gray-600">
                <InfoTip text="Filters the list below to notifications sent within this time range. Pending/stopped follow-ups always show regardless of window — they're action items, not history." />
              </span>
              <WindowTabs value={notifWindow} onChange={setNotifWindow} />
              <button onClick={load} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-800 text-gray-300 border border-gray-700 hover:bg-gray-700">
                ↻ Refresh
              </button>
            </div>
          </div>

          {/* Full admin control — trigger a fresh notification now, or mute/unmute everything for this user */}
          <div className="flex items-center gap-2 flex-shrink-0 bg-gray-900 border border-gray-800 rounded-xl px-3 py-2">
            <span className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold flex items-center">
              Admin control
              <InfoTip text="Trigger sends this user a fresh notification right now, bypassing silent hours/budget/cap. Mute stops every future notification (auto-triggers and backoff follow-ups) for this user until you unmute — sent history is unaffected." />
            </span>
            <button onClick={triggerNowForUser} disabled={triggerLoading || muted}
              title={muted ? "Unmute this user first" : "Force-send a notification right now"}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50">
              {triggerLoading ? "Sending…" : "🔔 Trigger Notification"}
            </button>
            <button onClick={toggleMuteForUser} disabled={muteLoading}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-50 ${
                muted ? "bg-emerald-700 hover:bg-emerald-600 text-white" : "bg-red-800 hover:bg-red-700 text-white"
              }`}>
              {muteLoading ? "Working…" : muted ? "🔊 Unmute Notifications" : "🔇 Stop All Notifications"}
            </button>
            {muted && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-900/40 text-red-300 border border-red-800/40">Muted</span>}
            {controlMsg && <span className="text-[11px] text-gray-500 ml-1">{controlMsg}</span>}
          </div>

          {/* One tab per trigger type — buyer gets 2, seller gets 3 */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="flex gap-1 p-0.5 bg-gray-800/60 rounded-lg border border-gray-700/50 w-fit">
              {TABS_FOR_ROLE[pickerRole].map((t) => (
                <button key={t.trigger_type} onClick={() => setActiveType(t.trigger_type)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                    activeType === t.trigger_type ? "bg-indigo-500/20 text-indigo-300 border border-indigo-500/30" : "text-gray-500 hover:text-gray-300"
                  }`}>
                  {t.trigger_type === "new_listing" ? "🆕 " : t.trigger_type === "new_listing_followup" ? "🏠 " : t.trigger_type === "validity_missing" ? "📋 " : t.trigger_type === "pricing_nudge" ? "💰 " : "🎯 "}
                  {t.label}
                </button>
              ))}
            </div>
            <InfoTip text={
              activeType === "new_listing"
                ? "Fired the moment a seller uploads a deal that matches this user (they see it as a buyer, even if they're also a seller)."
                : activeType === "new_listing_followup"
                  ? "Repeat-until-seen reminder for that same matched deal, on the backoff cadence above — stops once opened."
                  : activeType === "validity_missing"
                    ? "Fired when this seller's own deal has no validity/expiry dates set — up to 2 times on the backoff cadence above, stops once dates are added or the deal goes inactive. Buyers never get these."
                    : activeType === "pricing_nudge"
                      ? "One of 6 pricing-intelligence nudges (underpriced, competitor undercut, seasonal markup, premium pricing, conversion-optimal, stale price) — see PricingIntelligenceEngine.analyze. Title/body edited below in Pricing Nudge Wording. Buyers never get these."
                      : "This seller's own lead alerts (+ their repeat-until-seen follow-up) — buyers never get these."
            } />
          </div>

          {error && <p className="text-red-400 text-xs font-mono bg-red-950/40 border border-red-800/40 rounded-lg px-3 py-2">{error}</p>}

          {/* THE list — everything sent + (on "All time") every current suggestion. This is
              the ONLY thing the window filter above controls; it's the primary content of
              this tab, deliberately shown before the (collapsed) template editor below. */}
          <p className="text-xs font-bold text-white uppercase tracking-wider px-1 flex-shrink-0">
            📋 All Notifications ({visibleItems.length}) — window: {notifWindow === "all" ? "All time" : notifWindow}
          </p>
          <div className="flex-1 bg-gray-900 border border-gray-800 rounded-xl overflow-y-auto overflow-x-auto min-h-[200px]">
            {loading || candidatesLoading
              ? <div className="flex items-center justify-center h-32 gap-2 text-gray-500 text-sm"><Spin /><Spinner /></div>
              : visibleItems.length === 0
                ? <div className="flex items-center justify-center h-32 text-gray-600 text-sm">No notifications in this window</div>
                : <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-gray-900 border-b border-gray-800">
                      <tr className="text-gray-500 text-left">
                        <th className="px-3 py-2">Type</th>
                        <th className="px-3 py-2">
                          Title / Display Notification
                          <InfoTip text="The real copy this notification was (or would be) sent with, including the globally-edited common line from the Notification Templates panel above. Read-only here — copy is edited once, globally, not per notification." />
                        </th>
                        <th className="px-3 py-2">When</th>
                        <th className="px-3 py-2">Status</th>
                        <th className="px-3 py-2">
                          Opened
                          <InfoTip text="Whether the user actually tapped/viewed this push in their notification bell. Opened notifications never get a repeat follow-up push — only unopened ones get retried on the backoff cadence above." />
                        </th>
                        <th className="px-3 py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleItems.map((n) => {
                        const key = rowKey(n);
                        return (
                          <tr key={key} className="border-b border-gray-800/60 hover:bg-gray-800/30 align-top">
                            <td className="px-3 py-2 text-gray-500">{n.type}</td>
                            <td className="px-3 py-2 max-w-xs">
                              <p className="text-white truncate">{n.title || "—"}</p>
                              <p className="text-gray-500 truncate">{n.body || ""}</p>
                            </td>
                            <td className="px-3 py-2 text-gray-500">
                              {n.status === "suggested" ? "not sent yet" : n.status === "sent" ? ago(n.sent_at) : n.next_due_at ? `due ${ago(n.next_due_at)}` : "—"}
                              {typeof n.stage === "number" && <span className="block text-gray-700">stage {n.stage + 1}</span>}
                            </td>
                            <td className="px-3 py-2">
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${STATUS_COLOR[n.status]}`}>{n.status}</span>
                            </td>
                            <td className="px-3 py-2">
                              {n.status === "suggested"
                                ? <span className="text-gray-700">—</span>
                                : n.opened == null
                                  ? <span className="text-gray-700">—</span>
                                  : n.opened
                                    ? <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-green-900/40 text-green-300 border-green-800/40">✓ Opened</span>
                                    : <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-amber-900/40 text-amber-300 border-amber-800/40">○ Not opened</span>
                              }
                            </td>
                            <td className="px-3 py-2">
                              {n.status === "suggested"
                                ? <button
                                    disabled={candidateBusyId === n.candidate_id}
                                    onClick={() => triggerCandidate({ candidate_id: n.candidate_id!, title: n.title || "", body: n.body || "", already_sent: false })}
                                    className="px-2 py-1 rounded bg-indigo-700 hover:bg-indigo-600 text-white text-[10px] font-semibold disabled:opacity-50">
                                    {candidateBusyId === n.candidate_id ? "Sending…" : "🔔 Trigger"}
                                  </button>
                                : n.state_id
                                  ? <div className="flex gap-1.5 flex-wrap">
                                      <button disabled={busyKey === key} onClick={() => triggerNow(n)}
                                        className="px-2 py-1 rounded bg-indigo-700 hover:bg-indigo-600 text-white text-[10px] font-semibold disabled:opacity-50">Trigger now</button>
                                      {n.status !== "stopped" && (
                                        <button disabled={busyKey === key} onClick={() => stopNotif(n)}
                                          className="px-2 py-1 rounded bg-red-800 hover:bg-red-700 text-white text-[10px] font-semibold disabled:opacity-50">Stop</button>
                                      )}
                                    </div>
                                  : <span className="text-gray-700">—</span>
                              }
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
            }
          </div>

        </>)}
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const AdminRecommendationsPage = () => {
  const [tab,      setTab]      = useState<"buyer" | "seller" | "notifications" | "settings">("buyer");
  const [users,    setUsers]    = useState<AdminUser[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState("");
  const [selected, setSelected] = useState<AdminUser | null>(null);
  const [page,     setPage]     = useState(1);
  const [hasMore,  setHasMore]  = useState(false);
  const [wsStatus,      setWsStatus]      = useState<"connecting" | "connected" | "disconnected">("connecting");
  const [wsEvents,      setWsEvents]      = useState<{ type: string; label: string; ts: Date }[]>([]);
  const [latestWsEvent, setLatestWsEvent] = useState<Record<string, unknown> | null>(null);
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    let dead = false;
    let retryMs = 2000;

    const connect = () => {
      if (dead) return;
      // No literal fallback: rec_server verifies this JWT's signature and
      // requires super_admin, so a placeholder is closed with 4001 and the
      // retry loop would reconnect forever.
      const token = adminStorage.getToken();
      if (!token) {
        setWsStatus("disconnected");
        return;
      }
      const base  = (import.meta.env.VITE_REC_API_BASE_URL || "http://localhost:8003")
        .replace(/^http/, "ws");
      const ws = new WebSocket(`${base}/ws/admin?token=${encodeURIComponent(token)}`);
      wsRef.current = ws;
      setWsStatus("connecting");

      ws.onopen = () => { setWsStatus("connected"); retryMs = 2000; };

      ws.onmessage = (e) => {
        try {
          const ev = JSON.parse(e.data);
          const labels: Record<string, string> = {
            lead_routed:        "Lead routed",
            lead_created:       "New lead",
            signals_computed:   "Signals updated",
            new_listing_fired:  "New listing push",
            demand_gap_found:   "Demand gap found",
            recs_updated:       "Recs updated",
            notification_added: "Notification sent",
          };
          setWsEvents((prev) => [
            { type: ev.type || "event", label: labels[ev.type] || ev.type, ts: new Date() },
            ...prev.slice(0, 19),
          ]);
          setLatestWsEvent(ev);
        } catch { /* ignore malformed */ }
      };

      ws.onclose = () => {
        setWsStatus("disconnected");
        if (!dead) setTimeout(connect, retryMs = Math.min(retryMs * 2, 30000));
      };

      ws.onerror = () => ws.close();
    };

    connect();
    return () => { dead = true; wsRef.current?.close(); };
  }, []);

  const fetchUsers = useCallback(async (role: string, pg: number, q: string) => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { role, page: pg, page_size: 30 };
      if (q) params.search = q;
      const { data } = await apiClient.get<any>("/api/admin/users", { params, headers: tok() });
      const list: AdminUser[] = data.items || data.users || data.data || [];
      setUsers(pg === 1 ? list : (prev) => [...prev, ...list]);
      setHasMore((data.page ?? pg) < (data.total_pages ?? data.pages ?? 1));
    } catch { setUsers([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (tab === "notifications" || tab === "settings") return;
    setUsers([]); setSelected(null); setPage(1);
    fetchUsers(tab, 1, search);
  }, [tab]);

  useEffect(() => {
    if (tab === "notifications" || tab === "settings") return;
    const t = setTimeout(() => { setUsers([]); setPage(1); fetchUsers(tab, 1, search); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const [backfillState, setBackfillState] = useState<"idle" | "running" | "done" | "error">("idle");
  const [backfillResult, setBackfillResult] = useState<{ processed: number; updated: number } | null>(null);
  const [debugData, setDebugData]   = useState<any>(null);
  const [debugOpen, setDebugOpen]   = useState(false);
  useBodyScrollLock(debugOpen);
  const [debugLoading, setDebugLoading] = useState(false);

  const runBackfill = useCallback(async () => {
    setBackfillState("running");
    try {
      const { data } = await recApiClient.post<any>("/api/seller-intel/admin/backfill-titles");
      setBackfillResult({ processed: data.processed ?? 0, updated: data.updated ?? 0 });
      setBackfillState("done");
    } catch {
      setBackfillState("error");
    }
  }, []);

  const runDebug = useCallback(async (sellerId: string) => {
    setDebugLoading(true);
    setDebugOpen(true);
    try {
      const { data } = await recApiClient.get<any>(`/api/seller-intel/admin/debug/${sellerId}`);
      setDebugData(data);
    } catch (e: any) {
      setDebugData({ error: e?.message || "request failed" });
    } finally {
      setDebugLoading(false);
    }
  }, []);

  return (
    <div className="h-[calc(100vh-80px)] flex flex-col gap-4">

      <AdminPageHeader
        className="mb-0 flex-shrink-0"
        title="Recommendation Engine"
        subtitle="Formula A · B · C · Charts live at 1s · Seller = seller + buyer roles"
        actions={
        <div className="flex flex-col items-start sm:items-end gap-1">
          <div className="flex items-center gap-2">
            {(tab === "buyer" || tab === "seller") && (
              <RefreshButton onRefresh={() => fetchUsers(tab, page, search)} />
            )}
            <button
              onClick={runBackfill}
              disabled={backfillState === "running"}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                backfillState === "running" ? "bg-gray-800 text-gray-500 border-gray-700 cursor-wait" :
                backfillState === "done"    ? "bg-green-900/40 text-green-300 border-green-800/40 cursor-default" :
                backfillState === "error"   ? "bg-red-900/40 text-red-300 border-red-800/40 hover:bg-red-900/60" :
                                             "bg-gray-800 text-gray-300 border-gray-700 hover:bg-gray-700"
              }`}
            >
              {backfillState === "running" ? "Backfilling…" :
               backfillState === "done"    ? `✓ Backfill done (${backfillResult?.updated}/${backfillResult?.processed})` :
               backfillState === "error"   ? "Backfill failed — retry" :
                                            "Backfill Deal Titles"}
            </button>
            {tab === "seller" && selected && (
              <button
                onClick={() => runDebug(selected.id)}
                disabled={debugLoading}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold border bg-gray-800 text-gray-300 border-gray-700 hover:bg-gray-700 transition-all"
              >
                {debugLoading ? "Loading…" : "🔍 Debug DB"}
              </button>
            )}
          </div>

          {/* Debug modal */}
          {debugOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={() => setDebugOpen(false)}>
              <div className="bg-gray-900 border border-gray-700 rounded-2xl w-[780px] max-h-[80vh] overflow-hidden flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-800">
                  <p className="text-white font-semibold text-sm">MongoDB Debug — {selected?.name || selected?.email}</p>
                  <button onClick={() => setDebugOpen(false)} className="text-gray-500 hover:text-white text-lg">✕</button>
                </div>
                <div className="overflow-auto flex-1 p-4">
                  {debugLoading
                    ? <p className="text-gray-500 text-sm text-center py-8">Fetching from MongoDB…</p>
                    : debugData
                      ? (<>
                          {/* Lead inbox check */}
                          <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2">Lead Inbox — Buyer Name Lookup</p>
                          {(debugData.lead_inbox_check || []).map((l: any, i: number) => (
                            <div key={i} className="bg-gray-800/60 rounded-lg px-3 py-2 mb-2 text-xs space-y-0.5">
                              <div className="flex gap-4 flex-wrap">
                                <span className="text-gray-500">lead_id: <span className="text-gray-300 font-mono">{l.lead_id?.slice(-8)}</span></span>
                                <span className="text-gray-500">user_id_in_lead: <span className="text-amber-300 font-mono">{l.user_id_in_lead || "EMPTY"}</span></span>
                                <span className={`font-semibold ${l.user_found ? "text-green-400" : "text-red-400"}`}>{l.user_found ? "✓ user found" : "✗ user NOT found"}</span>
                              </div>
                              {l.user_found && (
                                <div className="flex gap-4 flex-wrap">
                                  <span className="text-gray-500">name: <span className="text-white">{l.user_name || "—"}</span></span>
                                  <span className="text-gray-500">email: <span className="text-white">{l.user_email || "—"}</span></span>
                                  <span className="text-gray-500">_id: <span className="text-gray-400 font-mono">{l.user_id_field}</span></span>
                                </div>
                              )}
                            </div>
                          ))}
                          {/* feed_metadata check */}
                          <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2 mt-4">feed_metadata — Deal Title Coverage</p>
                          <div className="bg-gray-800/60 rounded-lg px-3 py-2 mb-2 text-xs space-y-1">
                            <div className="flex gap-4">
                              <span className="text-gray-500">total docs: <span className="text-white">{debugData.feed_metadata?.total_docs}</span></span>
                              <span className={`font-semibold ${(debugData.feed_metadata?.missing_title || 0) > 0 ? "text-amber-400" : "text-green-400"}`}>
                                missing title: {debugData.feed_metadata?.missing_title}
                              </span>
                            </div>
                            {debugData.feed_metadata?.sample_missing_doc && (<>
                              <p className="text-gray-500">sample doc missing title:</p>
                              <div className="flex gap-4 flex-wrap pl-2">
                                <span className="text-gray-500">feed_id: <span className="font-mono text-gray-300">{debugData.feed_metadata.sample_missing_doc.feed_id?.slice(-8)}</span></span>
                                <span className="text-gray-500">title_in_feed_metadata: <span className="text-red-400">{String(debugData.feed_metadata.sample_missing_doc.title_in_feed_metadata ?? "null")}</span></span>
                                <span className="text-gray-500">title_in_prod_deals: <span className={debugData.feed_metadata.sample_missing_doc.title_in_prod_deals ? "text-green-400" : "text-red-400"}>{String(debugData.feed_metadata.sample_missing_doc.title_in_prod_deals ?? "null")}</span></span>
                              </div>
                              {(debugData.feed_metadata?.missing_title || 0) > 0 && (
                                <p className="text-amber-400 text-[10px] mt-1">→ Click "Backfill Deal Titles" to fix {debugData.feed_metadata.missing_title} docs</p>
                              )}
                            </>)}
                          </div>
                        </>)
                      : <p className="text-gray-600 text-sm text-center py-8">No data</p>
                  }
                </div>
              </div>
            </div>
          )}
          <div className="flex items-center gap-1.5 text-xs">
            <span className={`w-2 h-2 rounded-full ${
              wsStatus === "connected"    ? "bg-green-500 animate-pulse" :
              wsStatus === "connecting"   ? "bg-yellow-500 animate-pulse" :
                                           "bg-red-500"
            }`} />
            <span className={
              wsStatus === "connected"  ? "text-green-400" :
              wsStatus === "connecting" ? "text-yellow-400" :
                                         "text-red-400"
            }>
              rec-ws {wsStatus}
            </span>
          </div>
          {wsEvents.length > 0 && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <span className="text-gray-600">last:</span>
              <span className="text-gray-400">{wsEvents[0].label}</span>
              <span className="text-gray-600">{wsEvents[0].ts.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
        }
      />

      <div className="flex flex-wrap gap-1 bg-gray-900 p-1 rounded-xl w-fit max-w-full border border-gray-800 flex-shrink-0">
        {(["buyer", "seller", "notifications", "settings"] as const).map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
              tab === t
                ? t === "buyer" ? "bg-indigo-600 text-white shadow" : t === "seller" ? "bg-amber-600 text-white shadow" : t === "notifications" ? "bg-violet-600 text-white shadow" : "bg-teal-600 text-white shadow"
                : "text-gray-400 hover:text-white"
            }`}>
            {t === "buyer" ? "👤 Buyers" : t === "seller" ? "🏢 Sellers" : t === "notifications" ? "🔔 Notifications" : "⚙️ Notification Settings"}
          </button>
        ))}
      </div>

      {tab === "notifications" ? (
        <NotificationsPanel />
      ) : tab === "settings" ? (
        <NotificationSettingsPanel />
      ) : (
      <div className="flex-1 flex gap-4 min-h-0">

        {/* User list */}
        <div className="w-72 flex flex-col bg-gray-900 border border-gray-800 rounded-xl overflow-hidden flex-shrink-0">
          <div className="p-3 border-b border-gray-800 flex-shrink-0">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search ${tab}s…`}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading && users.length === 0
              ? <div className="flex items-center justify-center h-24 gap-2 text-gray-500 text-sm"><Spin /><Spinner /></div>
              : users.length === 0
                ? <div className="flex items-center justify-center h-24 text-gray-600 text-sm">No {tab}s found</div>
                : <>
                    {users.map((u) => (
                      <button key={u.id} onClick={() => setSelected(u)}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-800/60 transition-colors border-b border-gray-800/40 ${
                          selected?.id === u.id ? "bg-gray-800 border-l-2 border-l-indigo-500" : ""
                        }`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0 ${
                          tab === "buyer" ? "bg-indigo-500/20 text-indigo-400" : "bg-amber-500/20 text-amber-400"
                        }`}>{u.name[0]?.toUpperCase()}</div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-white truncate">{u.name}</p>
                          <p className="text-xs text-gray-500 truncate">{u.email}</p>
                        </div>
                      </button>
                    ))}
                    {hasMore && (
                      <button onClick={() => { const np = page + 1; setPage(np); fetchUsers(tab, np, search); }}
                        className="w-full py-3 text-xs text-indigo-400 hover:text-indigo-300 hover:bg-gray-800/40 transition-colors">
                        Load more
                      </button>
                    )}
                  </>
            }
          </div>
          <div className="border-t border-gray-800 px-4 py-2 flex items-center gap-2 flex-shrink-0">
            <Pulse />
            <p className="text-xs text-gray-600">{users.length} {tab}s</p>
          </div>
        </div>

        {/* Detail pane */}
        <div className="flex-1 bg-gray-950 border border-gray-800 rounded-xl overflow-y-auto">
          {!selected
            ? <div className="flex flex-col items-center justify-center h-full text-center px-8">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 ${
                  tab === "buyer" ? "bg-indigo-500/10" : "bg-amber-500/10"
                }`}>
                  <svg className={`w-8 h-8 ${tab === "buyer" ? "text-indigo-400" : "text-amber-400"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <p className="text-gray-400 font-medium">Select a {tab}</p>
                <p className="text-gray-600 text-sm mt-1">
                  {tab === "buyer"
                    ? "Live charts · Formula A · interest graph · seasonal patterns · DMC affinity · engagement radar"
                    : "Live charts · Formula B + C · full seller signals · buyer persona section · performance radar"}
                </p>
                <p className="text-gray-700 text-xs mt-3">Charts update every 1s · Data refreshes via WebSocket push</p>
              </div>
            : <div className="p-5">
                {tab === "buyer"
                  ? <BuyerPanel  key={selected.id} user={selected} wsEvent={latestWsEvent} />
                  : <SellerPanel key={selected.id} user={selected} wsEvent={latestWsEvent} />
                }
              </div>
          }
        </div>
      </div>
      )}
    </div>
  );
};

export default AdminRecommendationsPage;
